import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Job, Issue, Contractor, ContractorOpportunity, CustomerCommEntry, CustomerCredit, MaterialEvent } from "./types";
import type { BuildReviewEvent } from "./buildReviewTypes";
import type { Opportunity, FollowUpTask } from "./opportunityTypes";
import type { PurchaseOrder, StagingRecord, LoadOutChecklist } from "./warehouseExecution";
import type { Conversation, Message } from "./communicationTypes";
import type { ServiceTicket } from "./serviceTicketTypes";
import type { JobMessage } from "./jobMessageTypes";
import type { InstallerWrapRecord } from "./installerWrapEngine";
import type { InstallFollowUpTask } from "./unifiedFollowUp";
import type { AppointmentResult } from "./appointmentResultTypes";
import type { AppointmentOutcomeExtension } from "./appointmentOutcomeTypes";
import type { ContractorPayProfile } from "./contractorPayProfile";
import type { SystemEvent, SystemEventType } from "./communicationEngine";
import type { ContractorInterest } from "./contractor/contractorInterest";
import type { MarketplaceOffer } from "./contractor/marketplaceOffer";
import type { Crew } from "./contractor/crew";
import type { JobSpecExecution, PhaseExecution, ChecklistResponse, PhaseStatus } from "./jobSpecExecution";
import type { MaterialKit } from "./materialKit";
import type { SpecExecutionOutcome } from "./specExecutionOutcome";
import { deriveOutcome, upsertOutcome } from "./specExecutionOutcome";
import { getPayProfile } from "./contractorPayProfile";
import { bindSpec, getCanonicalSpecForArchetype, resolveBoundSpec, type BoundJobSpec, type JobSpec, type JobSpecBomItem, type JobSpecPhaseName, type SpecArchetype } from "./jobSpec";
import { deriveBoundSpecForJob } from "@/services/bound-spec.service";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { JobService } from "@/services/job.service";
import { IssueService } from "@/services/issue.service";
import { ContractorService } from "@/services/contractor.service";
import { CustomerCommService } from "@/services/customer-comm.service";
import { CreditService } from "@/services/credit.service";
import { ServiceTicketService } from "@/services/service-ticket.service";
import { BuildReviewService } from "@/services/build-review.service";
import { FollowUpTaskService } from "@/services/follow-up-task.service";
import { AppointmentResultService } from "@/services/appointment-result.service";
import { dbAppointmentResultToAppointmentResult } from "@/mappers/appointment-result.mapper";
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { formatDisplayReference, formatLeadReference, type OpportunityStage } from "./opportunityTypes";
import { LeadSourceService, type LeadSource } from "@/services/leadSource.service";
import type { DbAppointmentResult } from "@/types/database";
import { ConsultationService, type ConsultationSessionData } from "@/services/consultation.service";
import { JobOperationsService, type SchedulingEventRow } from "@/services/job-operations.service";
import { CallbackService } from "@/services/callback.service";
import { HoldbackLedgerService } from "@/services/holdback-ledger.service";
import { DisputeEventService } from "@/services/dispute-event.service";
import { SoftSuggestionService } from "@/services/soft-suggestion.service";
import { OverrideOutcomeService } from "@/services/override-outcome.service";
import type { Callback } from "./contractor/networkExecution/callback";
import type { HoldbackLedgerEntry } from "./contractor/networkExecution/holdback/ledger";
import type { DisputeEvent } from "./contractor/networkExecution/dispute/dispute";
import type { SoftEnforcementSuggestion } from "./contractor/networkExecution/softEnforcement/suggestions";
import type { OverrideOutcomeRecord } from "./contractor/networkExecution/overrideRereview";
import { queryKeys } from "@/lib/queryKeys";
import { isCommandDashboardServerPagedRoute, isContractorOnboardingServerPagedRoute, isContractorsServerPagedRoute, isCustomersServerPagedRoute, isExecutionServerPagedRoute, isGrowthChannelsServerPagedRoute, isInstallerOffersServerPagedRoute, isIntakeReviewServerPagedRoute, isJobDetailServerPagedRoute, isOrderingQueueServerPagedRoute, isPermittingServerPagedRoute, isPipelineServerPagedRoute, isReportsServerPagedRoute, isResolveServerPagedRoute, isResolveServiceServerPagedRoute, isSchedulingDispatchServerPagedRoute, isWarehouseServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import { fetchProfileDisplayNames } from "@/lib/profileLookupCache";
import { observeQueryStart, recordQueryMetric, recordRealtimeMetric } from "@/lib/queryObservability";
import {
  APPDATA_INITIAL_FULL_REFRESH_ENABLED,
  APPDATA_APPOINTMENTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_COMMAND_DASHBOARD_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_COMMAND_DASHBOARD_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_CONTRACTORS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_CONTRACTORS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_CUSTOMERS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_CUSTOMERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_EXECUTION_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_EXECUTION_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_GROWTH_CHANNELS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_GROWTH_CHANNELS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_INSTALLER_OFFERS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_INSTALLER_OFFERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_INTAKE_REVIEW_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_INTAKE_REVIEW_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_JOB_DETAIL_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_JOB_DETAIL_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_ORDERING_QUEUE_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_ORDERING_QUEUE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_PERMITTING_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_PERMITTING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_PIPELINE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_PROJECTS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_PROJECTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_RESOLVE_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_RESOLVE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_RESOLVE_SERVICE_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_RESOLVE_SERVICE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_REPORTS_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_REPORTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_SCHEDULING_DISPATCH_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_SCHEDULING_DISPATCH_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED,
  APPDATA_WAREHOUSE_GLOBAL_JOBS_HYDRATION_ENABLED,
  APPDATA_SPEC_BACKFILL_ON_LOAD_ENABLED,
  APPOINTMENTS_SERVER_PAGING_ENABLED,
  COMMAND_DASHBOARD_SERVER_PAGING_ENABLED,
  CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED,
  CONTRACTORS_SERVER_PAGING_ENABLED,
  CUSTOMERS_SERVER_PAGING_ENABLED,
  DATA_CACHE_V2_ENABLED,
  EXECUTION_SERVER_PAGING_ENABLED,
  GROWTH_CHANNELS_SERVER_PAGING_ENABLED,
  INSTALLER_OFFERS_SERVER_PAGING_ENABLED,
  INTAKE_REVIEW_SERVER_PAGING_ENABLED,
  JOB_DETAIL_SERVER_QUERY_ENABLED,
  ORDERING_QUEUE_SERVER_PAGING_ENABLED,
  PERMITTING_SERVER_PAGING_ENABLED,
  PIPELINE_SERVER_PAGING_ENABLED,
  PROJECTS_SERVER_PAGING_ENABLED,
  QUERY_MODE,
  REPORTS_SERVER_PAGING_ENABLED,
  RESOLVE_SERVER_PAGING_ENABLED,
  RESOLVE_SERVICE_SERVER_PAGING_ENABLED,
  SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED,
  SUPABASE_SYNC_DEBUG,
  VISIBLE_SYNC_POLL_MS,
  WAREHOUSE_SERVER_PAGING_ENABLED,
} from "@/lib/dataRuntimeFlags";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";

type DbMarketplaceOfferRow = {
  id: string;
  job_id: string;
  contractor_id: string;
  status: MarketplaceOffer["status"] | string;
  sent_at: string;
  sent_by: string;
  expires_at: string;
  payout_amount_snapshot?: number | null;
  responded_at?: string | null;
  response_reason?: string | null;
  response_note?: string | null;
  audit?: unknown;
};

type DbContractorInterestRow = {
  id: string;
  job_id: string;
  contractor_id: string;
  status: ContractorInterest["status"] | string;
  submitted_at: string;
  closed_at?: string | null;
  close_reason?: string | null;
};

type DbJobMetadataRow = {
  id: string;
  metadata?: Record<string, unknown> | null;
};

type DbJobSpecExecutionRow = {
  job_id: string;
  spec_ref?: string | null;
  spec_version?: string | null;
  metadata?: Record<string, unknown> | null;
};

type DbJobSpecMaterialKitRow = {
  job_id: string;
  spec_ref?: string | null;
  spec_version?: string | null;
  metadata?: Record<string, unknown> | null;
};

type DbContractorMetadataRow = {
  id: string;
  metadata?: Record<string, unknown> | null;
};

type DbCrewRow = {
  id: string;
  contractor_id: string;
  name: string;
  lead_installer_name?: string | null;
  members?: string[] | null;
  active?: boolean | null;
};

type DbSystemEventRow = {
  id: string;
  organization_id: string;
  event_type: string;
  job_id: string | null;
  lead_id: string | null;
  quote_id: string | null;
  contractor_id: string | null;
  triggered_by: string | null;
  payload: Record<string, any>;
  created_at: string;
};

const MARKETPLACE_OFFER_STATUSES: ReadonlySet<MarketplaceOffer["status"]> = new Set([
  "pending",
  "accepted",
  "declined",
  "expired",
  "withdrawn",
  "superseded",
]);

const CONTRACTOR_INTEREST_STATUSES: ReadonlySet<ContractorInterest["status"]> = new Set([
  "open",
  "offer_sent",
  "dismissed",
  "stale",
]);

const CONTRACTOR_INTEREST_CLOSE_REASONS: ReadonlySet<NonNullable<ContractorInterest["closeReason"]>> = new Set([
  "offer_sent",
  "dispatcher_dismissed",
  "job_left_dispatch",
]);

function mapMarketplaceOfferRow(row: DbMarketplaceOfferRow): MarketplaceOffer | null {
  const status = String(row.status ?? "").trim().toLowerCase();
  if (!MARKETPLACE_OFFER_STATUSES.has(status as MarketplaceOffer["status"])) return null;
  return {
    id: row.id,
    jobId: row.job_id,
    contractorId: row.contractor_id,
    status: status as MarketplaceOffer["status"],
    sentAt: row.sent_at,
    sentBy: row.sent_by,
    expiresAt: row.expires_at,
    payoutAmountSnapshot:
      typeof row.payout_amount_snapshot === "number" ? row.payout_amount_snapshot : undefined,
    respondedAt: row.responded_at ?? undefined,
    responseReason: (row.response_reason ?? undefined) as MarketplaceOffer["responseReason"],
    responseNote: row.response_note ?? undefined,
    audit: Array.isArray(row.audit) ? (row.audit as MarketplaceOffer["audit"]) : [],
  };
}

function mapContractorInterestRow(row: DbContractorInterestRow): ContractorInterest | null {
  const status = String(row.status ?? "").trim();
  if (!CONTRACTOR_INTEREST_STATUSES.has(status as ContractorInterest["status"])) return null;
  const closeReasonRaw = String(row.close_reason ?? "").trim();
  return {
    id: row.id,
    jobId: row.job_id,
    contractorId: row.contractor_id,
    submittedAt: row.submitted_at,
    status: status as ContractorInterest["status"],
    closedAt: row.closed_at ?? undefined,
    closeReason: CONTRACTOR_INTEREST_CLOSE_REASONS.has(closeReasonRaw as NonNullable<ContractorInterest["closeReason"]>)
      ? (closeReasonRaw as NonNullable<ContractorInterest["closeReason"]>)
      : undefined,
  };
}

const SYSTEM_EVENT_TYPES: ReadonlySet<SystemEventType> = new Set<SystemEventType>([
  "job_created", "deposit_requested", "deposit_paid", "scheduled",
  "install_started", "wrap_submitted", "install_completed", "qa_passed",
  "payment_collected", "issue_created", "issue_resolved", "stage_changed",
  "invoice_created", "scheduling_change", "qa_submitted", "admin_override",
  "contractor_unassigned", "contractor_interest_submitted", "contractor_interest_closed",
]);

function mapSystemEventRow(row: DbSystemEventRow): SystemEvent | null {
  if (!SYSTEM_EVENT_TYPES.has(row.event_type as SystemEventType)) return null;
  return {
    type: row.event_type as SystemEventType,
    jobId: row.job_id ?? "",
    timestamp: row.created_at,
    payload: row.payload ?? {},
  };
}

function mapPayProfileFromContractorMetadata(
  row: DbContractorMetadataRow,
): ContractorPayProfile | null {
  const meta = row.metadata ?? {};
  const raw =
    (meta.payProfile as Record<string, unknown> | undefined) ??
    (meta.pay_profile as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== "object") return null;
  const nowIso = new Date().toISOString();
  const profileId = String(raw.id ?? "").trim() || `PP-${row.id}`;
  const allowedSpecTypesRaw = Array.isArray(raw.allowedSpecTypes)
    ? raw.allowedSpecTypes
    : Array.isArray(raw.allowed_spec_types)
      ? raw.allowed_spec_types
      : [];
  const allowedSpecTypes = allowedSpecTypesRaw
    .map((v) => String(v))
    .filter(Boolean) as ContractorPayProfile["allowedSpecTypes"];

  return {
    id: profileId,
    contractorId: String(raw.contractorId ?? raw.contractor_id ?? row.id),
    allowedSpecTypes,
    totalPayoutAmount: Number(raw.totalPayoutAmount ?? raw.total_payout_amount ?? 0),
    paymentStructure:
      String(raw.paymentStructure ?? raw.payment_structure ?? "full_completion") as ContractorPayProfile["paymentStructure"],
    paymentTrigger:
      String(raw.paymentTrigger ?? raw.payment_trigger ?? "qa_pass") as ContractorPayProfile["paymentTrigger"],
    paymentSlaHours: Number(raw.paymentSlaHours ?? raw.payment_sla_hours ?? 24),
    fieldAddCapAmount: Number(raw.fieldAddCapAmount ?? raw.field_add_cap_amount ?? 0),
    tierLevel: (raw.tierLevel ?? raw.tier_level) as ContractorPayProfile["tierLevel"],
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    createdAt: String(raw.createdAt ?? raw.created_at ?? nowIso),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? nowIso),
    notes: typeof raw.notes === "string" ? raw.notes : undefined,
  };
}

/**
 * Portal checklist slug → CCC JobSpecPhaseName.
 * The portal writes jobs.metadata.checklist keys as "{slug}:{0-based-index}".
 */
const PORTAL_SLUG_TO_PHASE_NAME: Record<string, JobSpecPhaseName> = {
  prep: "Arrival, Prep & Protection",
  demo: "Demolition & Structural Assessment",
  rough: "Framing, Plumbing & Rough-In",
  install: "Pan, Substrate & Wall Installation",
  finish: "Trim, Fixtures & Waterproof Finishing",
  qa: "Final QA, Water Testing & Closeout",
};

/** Reverse map: JobSpecPhaseName → portal slug, for augmenting existing executions. */
const PHASE_NAME_TO_PORTAL_SLUG_CTX: Partial<Record<JobSpecPhaseName, string>> = Object.fromEntries(
  Object.entries(PORTAL_SLUG_TO_PHASE_NAME).map(([slug, name]) => [name, slug]),
) as Partial<Record<JobSpecPhaseName, string>>;

/**
 * Applies portal checklist data to a JobSpecExecution (existing or new).
 * - For phases missing from exec or with status "not_started": derives status
 *   from portal checklist (qa_passed / in_progress) and marks spec items checked.
 * - Phases already recorded in CCC (in_progress, submitted, qa_passed, etc.)
 *   are preserved unchanged.
 * - Works for both new synthesis (exec = null) and augmenting existing records.
 */
function applyPortalChecklistToExec(
  jobId: string,
  boundJobSpec: BoundJobSpec,
  portalChecklist: Record<string, boolean>,
  existingExec: JobSpecExecution | null,
): JobSpecExecution | null {
  const spec = resolveBoundSpec(boundJobSpec);
  if (!spec) return existingExec;

  let changed = false;
  const phases: PhaseExecution[] = spec.phases.map((sp) => {
    const existing = existingExec?.phases.find((p) => p.phaseName === sp.name);
    // Preserve phases that Koredor has finalized (submitted for QA review, approved, or rejected).
    // Portal checklist updates can still drive in_progress → qa_passed transitions.
    const lockedStatuses: PhaseStatus[] = ["submitted", "qa_passed", "qa_failed"];
    if (existing && lockedStatuses.includes(existing.status)) return existing;

    const slug = PHASE_NAME_TO_PORTAL_SLUG_CTX[sp.name];
    if (!slug) return existing ?? { phaseName: sp.name, status: "not_started" as PhaseStatus, photos: [], checklist: [] };

    const prefix = slug + ":";
    const entries = Object.entries(portalChecklist).filter(([k]) => k.startsWith(prefix));
    if (entries.length === 0) return existing ?? { phaseName: sp.name, status: "not_started" as PhaseStatus, photos: [], checklist: [] };

    const doneCount = entries.filter(([, v]) => v).length;
    if (doneCount === 0) return existing ?? { phaseName: sp.name, status: "not_started" as PhaseStatus, photos: [], checklist: [] };

    const allDone = doneCount === entries.length;
    const status: PhaseStatus = allDone ? "qa_passed" : "in_progress";
    const checklist: ChecklistResponse[] = allDone
      ? sp.checklistItems.map((item) => ({ itemId: item.id, checked: true, checkedAt: "portal" }))
      : (existing?.checklist ?? []);

    changed = true;
    return {
      phaseName: sp.name,
      status,
      photos: existing?.photos ?? [],
      checklist,
      ...(allDone ? { completedBy: "portal" } : {}),
    };
  });

  // If nothing changed and we have an existing exec, return it as-is.
  if (!changed && existingExec) return existingExec;
  // If nothing changed and no existing exec, no portal data was useful.
  if (!changed && !existingExec) return null;

  const allPhasesComplete =
    phases.length > 0 && phases.every((p) => p.status === "qa_passed");

  return {
    jobId,
    specRef: existingExec?.specRef ?? { specId: boundJobSpec.specId, versionNumber: boundJobSpec.versionNumber },
    phases,
    allPhasesComplete,
  };
}

/**
 * Fallback: synthesise a JobSpecExecution directly from the raw portal checklist map
 * when jobs.metadata.boundJobSpec is absent (e.g. manually-created test jobs).
 * Uses PORTAL_SLUG_TO_PHASE_NAME as the canonical phase list.
 */
function synthesizeExecFromPortalChecklist(
  jobId: string,
  checklist: Record<string, boolean>,
  existingExec: JobSpecExecution | null,
): JobSpecExecution | null {
  const lockedStatuses: PhaseStatus[] = ["submitted", "qa_passed", "qa_failed"];
  const phases: PhaseExecution[] = [];
  let changed = false;

  for (const [slug, phaseName] of Object.entries(PORTAL_SLUG_TO_PHASE_NAME)) {
    const prefix = slug + ":";
    const entries = Object.entries(checklist).filter(([k]) => k.startsWith(prefix));
    const existing = existingExec?.phases.find((p) => p.phaseName === phaseName);

    if (entries.length === 0) {
      if (existing) phases.push(existing);
      else phases.push({ phaseName, status: "not_started" as PhaseStatus, photos: [], checklist: [] });
      continue;
    }

    if (existing && lockedStatuses.includes(existing.status)) {
      phases.push(existing);
      continue;
    }

    const doneCount = entries.filter(([, v]) => v).length;
    if (doneCount === 0) {
      phases.push(existing ?? { phaseName, status: "not_started" as PhaseStatus, photos: [], checklist: [] });
      continue;
    }

    const allDone = doneCount === entries.length;
    const status: PhaseStatus = allDone ? "qa_passed" : "in_progress";
    changed = true;
    phases.push({
      phaseName,
      status,
      photos: existing?.photos ?? [],
      checklist: allDone
        ? [] // spec items unknown without boundJobSpec; portal completions are implicit
        : (existing?.checklist ?? []),
      ...(allDone ? { completedBy: "portal" } : {}),
    });
  }

  if (!changed && existingExec) return existingExec;
  if (!changed && !existingExec) return null;

  const allPhasesComplete = phases.length > 0 && phases.every((p) => p.status === "qa_passed");
  return {
    jobId,
    specRef: existingExec?.specRef ?? { specId: "portal-derived", versionNumber: 0 },
    phases,
    allPhasesComplete,
  };
}

function mapSpecExecutionFromJobMetadata(row: DbJobMetadataRow): JobSpecExecution | null {
  const meta = row.metadata ?? {};
  const raw =
    (meta.specExecution as Record<string, unknown> | undefined) ??
    (meta.spec_execution as Record<string, unknown> | undefined) ??
    (meta.jobSpecExecution as Record<string, unknown> | undefined) ??
    (meta.job_spec_execution as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== "object") return null;
  const jobId = String(raw.jobId ?? raw.job_id ?? row.id);
  const specRefRaw = (raw.specRef as Record<string, unknown> | undefined) ?? {};
  const specId = String(specRefRaw.specId ?? specRefRaw.spec_id ?? "").trim();
  const versionNumber = Number(specRefRaw.versionNumber ?? specRefRaw.version_number ?? 0);
  if (!jobId || !specId || versionNumber === null || versionNumber === undefined || Number.isNaN(versionNumber)) return null;
  const phases = Array.isArray(raw.phases) ? (raw.phases as JobSpecExecution["phases"]) : [];
  return {
    jobId,
    specRef: { specId, versionNumber },
    phases,
    allPhasesComplete: Boolean(raw.allPhasesComplete ?? raw.all_phases_complete ?? false),
  };
}

function mapSpecExecutionFromExecutionRow(row: DbJobSpecExecutionRow): JobSpecExecution | null {
  const raw = row.metadata ?? {};
  if (!raw || typeof raw !== "object") return null;
  const jobId = String((raw as Record<string, unknown>).jobId ?? (raw as Record<string, unknown>).job_id ?? row.job_id).trim();
  const specRefRaw = ((raw as Record<string, unknown>).specRef as Record<string, unknown> | undefined) ?? {};
  const specId = String(
    specRefRaw.specId ??
    specRefRaw.spec_id ??
    row.spec_ref ??
    "",
  ).trim();
  const versionNumberRaw = specRefRaw.versionNumber ?? specRefRaw.version_number ?? row.spec_version ?? 0;
  const versionNumber = Number(versionNumberRaw);
  if (!jobId || !specId || Number.isNaN(versionNumber)) return null;
  const phases = Array.isArray((raw as Record<string, unknown>).phases)
    ? ((raw as Record<string, unknown>).phases as JobSpecExecution["phases"])
    : [];
  return {
    jobId,
    specRef: { specId, versionNumber },
    phases,
    allPhasesComplete: Boolean(
      (raw as Record<string, unknown>).allPhasesComplete ??
      (raw as Record<string, unknown>).all_phases_complete ??
      false,
    ),
  };
}

function mapMaterialKitFromJobMetadata(row: DbJobMetadataRow): MaterialKit | null {
  const meta = row.metadata ?? {};
  const raw =
    (meta.materialKit as Record<string, unknown> | undefined) ??
    (meta.material_kit as Record<string, unknown> | undefined) ??
    (meta.jobMaterialKit as Record<string, unknown> | undefined) ??
    (meta.job_material_kit as Record<string, unknown> | undefined);
  if (!raw || typeof raw !== "object") return null;
  const jobId = String(raw.jobId ?? raw.job_id ?? row.id);
  const specRefRaw = (raw.specRef as Record<string, unknown> | undefined) ?? {};
  const specId = String(specRefRaw.specId ?? specRefRaw.spec_id ?? "").trim();
  const versionNumber = Number(specRefRaw.versionNumber ?? specRefRaw.version_number ?? 0);
  if (!jobId || !specId || versionNumber === null || versionNumber === undefined || Number.isNaN(versionNumber)) return null;
  const items = Array.isArray(raw.items) ? (raw.items as MaterialKit["items"]) : [];
  const override =
    raw.override && typeof raw.override === "object"
      ? (raw.override as MaterialKit["override"])
      : undefined;
  return {
    jobId,
    specRef: { specId, versionNumber },
    items,
    override,
  };
}

function mapMaterialKitFromKitRow(row: DbJobSpecMaterialKitRow): MaterialKit | null {
  const raw = row.metadata ?? {};
  if (!raw || typeof raw !== "object") return null;
  const jobId = String((raw as Record<string, unknown>).jobId ?? (raw as Record<string, unknown>).job_id ?? row.job_id).trim();
  const specRefRaw = ((raw as Record<string, unknown>).specRef as Record<string, unknown> | undefined) ?? {};
  const specId = String(
    specRefRaw.specId ??
    specRefRaw.spec_id ??
    row.spec_ref ??
    "",
  ).trim();
  const versionNumberRaw = specRefRaw.versionNumber ?? specRefRaw.version_number ?? row.spec_version ?? 0;
  const versionNumber = Number(versionNumberRaw);
  if (!jobId || !specId || Number.isNaN(versionNumber)) return null;
  const items = Array.isArray((raw as Record<string, unknown>).items)
    ? ((raw as Record<string, unknown>).items as MaterialKit["items"])
    : [];
  const override =
    (raw as Record<string, unknown>).override && typeof (raw as Record<string, unknown>).override === "object"
      ? ((raw as Record<string, unknown>).override as MaterialKit["override"])
      : undefined;
  return {
    jobId,
    specRef: { specId, versionNumber },
    items,
    override,
  };
}

function mapCrewRow(row: DbCrewRow): Crew {
  return {
    id: row.id,
    contractorId: row.contractor_id,
    name: row.name,
    leadInstallerName: String(row.lead_installer_name ?? "").trim() || "Lead Installer",
    members: Array.isArray(row.members) ? row.members.filter(Boolean) : [],
    active: row.active !== false,
  };
}

interface AppData {
  jobs: Job[];
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>;
  jobsLoading: boolean;
  issues: Issue[];
  setIssues: React.Dispatch<React.SetStateAction<Issue[]>>;
  issuesLoading: boolean;
  contractors: Contractor[];
  setContractors: React.Dispatch<React.SetStateAction<Contractor[]>>;
  contractorsLoading: boolean;
  crews: Crew[];
  setCrews: React.Dispatch<React.SetStateAction<Crew[]>>;
  // Contractor bid opportunities (legacy name preserved)
  opportunities: ContractorOpportunity[];
  setOpportunities: React.Dispatch<React.SetStateAction<ContractorOpportunity[]>>;
  customerComms: CustomerCommEntry[];
  setCustomerComms: React.Dispatch<React.SetStateAction<CustomerCommEntry[]>>;
  customerCommsLoading: boolean;
  credits: CustomerCredit[];
  setCredits: React.Dispatch<React.SetStateAction<CustomerCredit[]>>;
  creditsLoading: boolean;

  // ── Network Execution ──
  callbacks: Callback[];
  holdbackLedger: HoldbackLedgerEntry[];
  disputeEvents: DisputeEvent[];
  softSuggestions: SoftEnforcementSuggestion[];
  overrideOutcomes: OverrideOutcomeRecord[];

  // ── Unified Sales Opportunity Store ──
  salesOpportunities: Opportunity[];
  salesOpportunitiesLoading: boolean;
  setSalesOpportunities: React.Dispatch<React.SetStateAction<Opportunity[]>>;
  /** @deprecated Use salesOpportunities */
  quotes: Opportunity[];
  /** @deprecated Use setSalesOpportunities */
  setQuotes: React.Dispatch<React.SetStateAction<Opportunity[]>>;
  /** @deprecated Use salesOpportunities */
  leads: Opportunity[];
  /** @deprecated Use setSalesOpportunities */
  setLeads: React.Dispatch<React.SetStateAction<Opportunity[]>>;

  // Warehouse execution
  purchaseOrders: PurchaseOrder[];
  setPurchaseOrders: React.Dispatch<React.SetStateAction<PurchaseOrder[]>>;
  stagingRecords: StagingRecord[];
  setStagingRecords: React.Dispatch<React.SetStateAction<StagingRecord[]>>;
  loadOutChecklists: LoadOutChecklist[];
  setLoadOutChecklists: React.Dispatch<React.SetStateAction<LoadOutChecklist[]>>;

  // Communication
  conversations: Conversation[];
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  conversationsLoading: boolean;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;

  // Service Tickets
  serviceTickets: ServiceTicket[];
  setServiceTickets: React.Dispatch<React.SetStateAction<ServiceTicket[]>>;
  serviceTicketsLoading: boolean;

  // Material Events
  materialEvents: MaterialEvent[];
  setMaterialEvents: React.Dispatch<React.SetStateAction<MaterialEvent[]>>;

  // Build Review Events
  buildReviewEvents: BuildReviewEvent[];
  setBuildReviewEvents: React.Dispatch<React.SetStateAction<BuildReviewEvent[]>>;
  buildReviewEventsLoading: boolean;
  setBuildReviewEventsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  /** @deprecated Use buildReviewEvents */
  bathReviewEvents: BuildReviewEvent[];
  /** @deprecated Use setBuildReviewEvents */
  setBathReviewEvents: React.Dispatch<React.SetStateAction<BuildReviewEvent[]>>;

  // Job Messages
  jobMessages: JobMessage[];
  setJobMessages: React.Dispatch<React.SetStateAction<JobMessage[]>>;
  jobMessagesLoading: boolean;

  // Lead Sources
  leadSources: LeadSource[];
  setLeadSources: React.Dispatch<React.SetStateAction<LeadSource[]>>;
  leadSourcesLoading: boolean;

  // Follow-up Tasks
  followUpTasks: FollowUpTask[];
  setFollowUpTasks: React.Dispatch<React.SetStateAction<FollowUpTask[]>>;

  // Installer Wrap Records
  installerWrapRecords: InstallerWrapRecord[];
  setInstallerWrapRecords: React.Dispatch<React.SetStateAction<InstallerWrapRecord[]>>;

  // Install Follow-Up Tasks
  installFollowUpTasks: InstallFollowUpTask[];
  setInstallFollowUpTasks: React.Dispatch<React.SetStateAction<InstallFollowUpTask[]>>;

  // Appointment Results
  appointmentResults: AppointmentResult[];
  setAppointmentResults: React.Dispatch<React.SetStateAction<AppointmentResult[]>>;

  // Consultation Sessions
  consultationSessions: ConsultationSessionData[];
  setConsultationSessions: React.Dispatch<React.SetStateAction<ConsultationSessionData[]>>;
  consultationSessionsLoading: boolean;

  // Appointment Outcome Extensions (Phase 1 — sidecar to AppointmentResult).
  // Hard-immutable: addAppointmentOutcomeExtension throws if a record with
  // the same appointmentResultId already exists. This matches the
  // proof-based execution model — outcomes are a system-of-record, not a
  // scratchpad.
  appointmentOutcomeExtensions: AppointmentOutcomeExtension[];
  addAppointmentOutcomeExtension: (extension: AppointmentOutcomeExtension) => void;

  // Contractor Pay Profiles — required before assignment
  payProfiles: ContractorPayProfile[];
  setPayProfiles: React.Dispatch<React.SetStateAction<ContractorPayProfile[]>>;

  // JobSpec Executions — installer/QA per-phase progress driven by bound spec
  specExecutions: JobSpecExecution[];
  setSpecExecutions: React.Dispatch<React.SetStateAction<JobSpecExecution[]>>;

  // Material Kits — derived from JobSpec BOM, gates install phase
  materialKits: MaterialKit[];
  setMaterialKits: React.Dispatch<React.SetStateAction<MaterialKit[]>>;

  // Spec Execution Outcomes — immutable v1 outcome dataset, auto-derived
  // when terminal QA PASS is reached. Read-only from consumers — the only
  // writer is the auto-derivation effect inside the provider.
  specOutcomes: SpecExecutionOutcome[];

  // Marketplace Offers (P1) — time-boxed assignment proposals.
  marketplaceOffers: MarketplaceOffer[];
  setMarketplaceOffers: React.Dispatch<React.SetStateAction<MarketplaceOffer[]>>;

  // Contractor Interest records (P3).
  contractorInterests: ContractorInterest[];
  setContractorInterests: React.Dispatch<React.SetStateAction<ContractorInterest[]>>;
  // Contractor onboarding completion (portal tour)
  markContractorOnboardingComplete: (contractorId: string) => Promise<void>;

  // System Events timeline.
  systemEvents: SystemEvent[];
  setSystemEvents: React.Dispatch<React.SetStateAction<SystemEvent[]>>;
  appendSystemEvents: (events: SystemEvent[]) => void;

  // Explicit DB refresh trigger for page-entry freshness.
  refreshData: () => Promise<void>;
  // True while explicit latest-data refresh is in-flight.
  isRefreshing: boolean;
  // Timestamp of latest successful explicit refresh completion.
  lastRefreshedAt: string | null;
  // True while contractor portal metadata writes are being persisted.
  contractorPortalSyncing: boolean;
  // Timestamp of the last successful contractor portal metadata save.
  contractorPortalLastSavedAt: string | null;
}

const AppDataContext = createContext<AppData | undefined>(undefined);
const FULL_REFRESH_BACKGROUND_DEBOUNCE_MS = 2_500;
const FULL_REFRESH_BACKGROUND_COOLDOWN_MS = 15_000;
const JOBS_REALTIME_REFRESH_DEBOUNCE_MS = 3_000;

function getMaxJobUpdatedAt(rows: Job[]): string | null {
  let maxMs = 0;
  let maxIso: string | null = null;
  for (const row of rows) {
    const raw = String(row.updatedAt ?? "").trim();
    if (!raw) continue;
    const ms = Date.parse(raw);
    if (Number.isNaN(ms) || ms < maxMs) continue;
    if (ms === maxMs && maxIso && raw <= maxIso) continue;
    maxMs = ms;
    maxIso = raw;
  }
  return maxIso;
}

function sortJobsByCreatedDesc(rows: Job[]): Job[] {
  return rows.slice().sort((a, b) => {
    const aMs = Date.parse(String(a.createdAt ?? ""));
    const bMs = Date.parse(String(b.createdAt ?? ""));
    if (Number.isNaN(aMs) && Number.isNaN(bMs)) return String(b.id).localeCompare(String(a.id));
    if (Number.isNaN(aMs)) return 1;
    if (Number.isNaN(bMs)) return -1;
    return bMs - aMs;
  });
}

type JobsData = Pick<
  AppData,
  | "jobs"
  | "setJobs"
  | "jobsLoading"
  | "issues"
  | "setIssues"
  | "issuesLoading"
  | "buildReviewEvents"
  | "setBuildReviewEvents"
  | "buildReviewEventsLoading"
  | "setBuildReviewEventsLoading"
  | "installerWrapRecords"
  | "setInstallerWrapRecords"
  | "installFollowUpTasks"
  | "setInstallFollowUpTasks"
  | "payProfiles"
  | "setPayProfiles"
  | "specExecutions"
  | "setSpecExecutions"
  | "materialKits"
  | "setMaterialKits"
  | "specOutcomes"
>;

type SalesData = Pick<
  AppData,
  | "salesOpportunities"
  | "setSalesOpportunities"
  | "quotes"
  | "setQuotes"
  | "leads"
  | "setLeads"
  | "appointmentResults"
  | "setAppointmentResults"
  | "appointmentOutcomeExtensions"
  | "addAppointmentOutcomeExtension"
  | "followUpTasks"
  | "setFollowUpTasks"
  | "leadSources"
  | "setLeadSources"
  | "leadSourcesLoading"
  | "consultationSessions"
  | "setConsultationSessions"
  | "consultationSessionsLoading"
>;

type OpsData = Pick<
  AppData,
  | "contractors"
  | "setContractors"
  | "contractorsLoading"
  | "crews"
  | "setCrews"
  | "customerComms"
  | "setCustomerComms"
  | "customerCommsLoading"
  | "serviceTickets"
  | "setServiceTickets"
  | "serviceTicketsLoading"
  | "conversations"
  | "setConversations"
  | "messages"
  | "setMessages"
  | "marketplaceOffers"
  | "setMarketplaceOffers"
  | "contractorInterests"
  | "setContractorInterests"
  | "systemEvents"
  | "setSystemEvents"
  | "appendSystemEvents"
  | "markContractorOnboardingComplete"
>;

type InventoryData = Pick<
  AppData,
  | "purchaseOrders"
  | "setPurchaseOrders"
  | "stagingRecords"
  | "setStagingRecords"
  | "loadOutChecklists"
  | "setLoadOutChecklists"
  | "materialEvents"
  | "setMaterialEvents"
>;

const JobsDataContext = createContext<JobsData | undefined>(undefined);
const SalesDataContext = createContext<SalesData | undefined>(undefined);
const OpsDataContext = createContext<OpsData | undefined>(undefined);
const InventoryDataContext = createContext<InventoryData | undefined>(undefined);

const DEFAULT_PROJECT_TYPE = "Other" as Job["projectType"];

function toStage(value: unknown): OpportunityStage {
  const v = String(value ?? "").trim();
  if (
    v === "Lead" ||
    v === "Quote Created" ||
    v === "Appointment Scheduled" ||
    v === "Appointment Completed" ||
    v === "Follow-Up Needed" ||
    v === "Closed Won" ||
    v === "Closed Lost"
  ) {
    return v;
  }
  return "Lead";
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function firstNonEmptyString(values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return "";
}

type HydrationRow = {
  id?: string;
  lead_id?: string;
  quote_number?: string;
  displayReference?: string;
  primary_name?: string;
  customer_name?: string;
  address?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip?: string;
  postal_code?: string;
  project_type?: string;
  appointment_time?: string;
  scheduled_at?: string;
  assigned_rep_name?: string;
  assigned_rep?: string;
  first_name?: string;
  last_name?: string;
  secondary_name?: string;
  primary_phone?: string;
  phone?: string;
  secondary_phone?: string;
  primary_email?: string;
  email?: string;
  secondary_email?: string;
  lead_source?: string;
  source_name?: string;
  stage?: string;
  created_at?: string;
  updated_at?: string;
  notes?: string;
  customer_id?: string;
  organization_id?: string;
  [key: string]: unknown;
};

function normalizeOpportunity(candidate: Partial<Opportunity> & Record<string, unknown>): Opportunity {
  const fallbackName = [
    candidate.first_name,
    candidate.last_name,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");

  const primaryName =
    String(
      candidate.primaryName ??
      candidate.customerName ??
      candidate.customer_name ??
      candidate.primary_name ??
      candidate.full_name ??
      candidate.name ??
      fallbackName,
    ).trim() ||
    "Unknown Customer";
  const primaryPhone =
    String(candidate.primaryPhone ?? candidate.customerPhone ?? candidate.primary_phone ?? "").trim();
  const primaryEmail =
    String(candidate.primaryEmail ?? candidate.customerEmail ?? candidate.primary_email ?? "").trim();
  const city = String(candidate.city ?? "").trim();
  const state = String(candidate.state ?? "").trim();
  const zip = String(candidate.zip ?? candidate.postal_code ?? "").trim();
  const street = String(candidate.address ?? candidate.address_line1 ?? "").trim();
  const projectTypeRaw = String(candidate.projectType ?? candidate.project_type ?? "").trim();
  const assignedRepRaw = firstNonEmptyString([
    candidate.assignedRep,
    candidate.assigned_rep_name,
    candidate.sales_rep_name,
    candidate.rep_name,
    candidate.rep_full_name,
    candidate.assigned_rep_display,
    candidate.createdBy,
    candidate.created_by,
    candidate.assigned_rep,
  ]);
  const assignedRep = assignedRepRaw && !isUuidLike(assignedRepRaw) ? assignedRepRaw : "";
  const createdAt = String(candidate.createdAt ?? candidate.created_at ?? new Date().toISOString());
  const updatedAt = String(candidate.updatedAt ?? candidate.updated_at ?? createdAt);
  const stage = toStage(candidate.stage ?? candidate.status);
  const id = String(candidate.id ?? candidate.quoteId ?? candidate.leadId ?? `OPP-${Date.now()}`);
  const rawDisplayReference = firstNonEmptyString([
    candidate.displayReference,
    candidate.display_reference,
    candidate.quote_number,
    candidate.quoteNumber,
    candidate.linked_quote_id,
  ]);
  const fallbackLeadId = firstNonEmptyString([candidate.leadId, candidate.lead_id]);
  const displayReference = rawDisplayReference
    ? (isUuidLike(rawDisplayReference)
      ? (fallbackLeadId ? formatLeadReference(fallbackLeadId) : "OPP-UNASSIGNED")
      : formatDisplayReference(rawDisplayReference))
    : (fallbackLeadId ? formatLeadReference(fallbackLeadId) : (isUuidLike(id) ? "OPP-UNASSIGNED" : formatDisplayReference(id)));

  return {
    id,
    quoteId: candidate.quoteId ?? null,
    leadId: candidate.leadId ?? null,
    displayReference,
    primaryName,
    secondaryName: String(candidate.secondaryName ?? candidate.secondary_name ?? ""),
    primaryPhone,
    secondaryPhone: String(candidate.secondaryPhone ?? candidate.secondary_phone ?? ""),
    primaryEmail,
    secondaryEmail: String(candidate.secondaryEmail ?? candidate.secondary_email ?? ""),
    address: street,
    city,
    state,
    zip,
    projectType: (projectTypeRaw || DEFAULT_PROJECT_TYPE) as Opportunity["projectType"],
    leadSource: String(candidate.leadSource ?? candidate.lead_source ?? candidate.source_name ?? ""),
    leadCost: Number(candidate.leadCost ?? candidate.lead_cost ?? 0) || undefined,
    stage,
    stageHistory: Array.isArray(candidate.stageHistory)
      ? (candidate.stageHistory as any[])
      : (Array.isArray(candidate.stage_history) ? (candidate.stage_history as any[]) : []),
    assignedRep,
    ownershipHistory: Array.isArray(candidate.ownershipHistory) ? (candidate.ownershipHistory as any[]) : [],
    activities: Array.isArray(candidate.activities) ? (candidate.activities as any[]) : [],
    appointmentTime:
      typeof candidate.appointmentTime === "string"
        ? candidate.appointmentTime
        : (typeof candidate.appointment_time === "string"
          ? candidate.appointment_time
          : (typeof candidate.scheduled_at === "string" ? candidate.scheduled_at : undefined)),
    projectConfig: (candidate.projectConfig ?? candidate.project_config) as any,
    totalEstimate: Number(candidate.totalEstimate ?? candidate.total_estimate ?? 0),
    linkedJobId: (candidate.linkedJobId ?? candidate.linked_job_id ?? candidate.convertedJobId) as string | undefined,
    createdAt,
    updatedAt,
    notes: String(candidate.notes ?? ""),
    wonAt: (candidate.wonAt ?? candidate.won_at) as string | undefined,
    wonOutcome: (candidate.wonOutcome ?? candidate.won_outcome) as any,
    lostAt: (candidate.lostAt ?? candidate.lost_at) as string | undefined,
    lostReason: (candidate.lostReason ?? candidate.lost_reason) as string | undefined,
    scopeOfWork: (candidate.scopeOfWork ?? candidate.scope_of_work) as string | undefined,
    pricingStatus: (candidate.pricingStatus ?? candidate.pricing_status) as any,
    pricingGuardrails: (candidate.pricingGuardrails ?? candidate.pricing_guardrails) as any,
    pricingOverride: (candidate.pricingOverride ?? candidate.pricing_override) as any,
    verification: (candidate.verification ?? candidate.verification_data) as any,
    contract: (candidate.contract ?? candidate.contract_document) as any,
    deposit: (candidate.deposit ?? candidate.deposit_info) as any,
    scenarios: (candidate.scenarios ?? candidate.quote_scenarios) as any,
    customerId: (candidate.customerId ?? candidate.customer_id) as string | undefined,
    organizationId: (candidate.organizationId ?? candidate.organization_id) as string | undefined,
    customerName: String(candidate.customerName ?? primaryName),
    customerPhone: String(candidate.customerPhone ?? primaryPhone),
    customerEmail: String(candidate.customerEmail ?? primaryEmail),
    customerAddress:
      String(candidate.customerAddress ?? "").trim() ||
      [street, city, [state, zip].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", "),
    createdBy: String(candidate.createdBy ?? candidate.created_by ?? assignedRep),
    status: stage,
    quoteName: String(candidate.quoteName ?? ""),
    quote_number: String(candidate.quote_number ?? ""),
    convertedJobId: (candidate.convertedJobId ?? candidate.linkedJobId) as string | undefined,
    appointmentId: (candidate.appointmentId ?? candidate.appointment_id) as string | undefined,
    linkedQuoteId: (candidate.linkedQuoteId ?? candidate.linked_quote_id) as string | undefined,
    tenantId: (candidate.tenantId ?? candidate.tenant_id) as string | undefined,
    decisionStatus: candidate.decisionStatus as string | null | undefined,
    validationStatus: candidate.validationStatus as string | null | undefined,
    rejectionReason: candidate.rejectionReason as string | null | undefined,
    rejectionNotes: candidate.rejectionNotes as string | null | undefined,
    acceptedAt: candidate.acceptedAt as string | null | undefined,
    rejectedAt: candidate.rejectedAt as string | null | undefined,
    isDuplicate: candidate.isDuplicate as boolean | null | undefined,
    matchedDuplicateBy: candidate.matchedDuplicateBy as string | null | undefined,
    // Keep normalized financial snapshots available for read-only summary/PDF flows.
    ...(candidate.subtotal !== undefined ? { subtotal: candidate.subtotal } : {}),
    ...(candidate.tax !== undefined ? { tax: candidate.tax } : {}),
    ...(candidate.discount !== undefined ? { discount: candidate.discount } : {}),
    ...(candidate.total !== undefined ? { total: candidate.total } : {}),
  };
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { pathname, search } = useBrowserLocationSnapshot();
  const validationMode = false;
  const validationSeed = null;

  const [jobs, setJobs] = useState<Job[]>(validationSeed?.jobs ?? []);
   const [jobsLoading, setJobsLoading] = useState(false);
  const [issues, setIssues] = useState<Issue[]>(validationSeed?.issues ?? []);
   const [issuesLoading, setIssuesLoading] = useState(false);
  const [contractors, setContractors] = useState<Contractor[]>([]);
   const [contractorsLoading, setContractorsLoading] = useState(false);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [contractorOpps, setContractorOpps] = useState<ContractorOpportunity[]>([]);
  const [customerComms, setCustomerComms] = useState<CustomerCommEntry[]>([]);
   const [customerCommsLoading, setCustomerCommsLoading] = useState(false);
  const [credits, setCredits] = useState<CustomerCredit[]>([]);
   const [creditsLoading, setCreditsLoading] = useState(false);
  const [callbacks, setCallbacks] = useState<Callback[]>([]);
  const [holdbackLedger, setHoldbackLedger] = useState<HoldbackLedgerEntry[]>([]);
  const [disputeEvents, setDisputeEvents] = useState<DisputeEvent[]>([]);
  const [softSuggestions, setSoftSuggestions] = useState<SoftEnforcementSuggestion[]>([]);
  const [overrideOutcomes, setOverrideOutcomes] = useState<OverrideOutcomeRecord[]>([]);

  // Single unified sales opportunity store
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [salesOpportunitiesLoading, setSalesOpportunitiesLoading] = useState(!validationMode);
  const shouldDeferAppointmentsOpportunityHydration =
    APPOINTMENTS_SERVER_PAGING_ENABLED &&
    !APPDATA_APPOINTMENTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    pathname === "/appointments";
  const shouldDeferProjectsOpportunityHydration =
    PROJECTS_SERVER_PAGING_ENABLED &&
    !APPDATA_PROJECTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    pathname === "/projects";
  const shouldDeferProjectsJobsHydration =
    PROJECTS_SERVER_PAGING_ENABLED &&
    !APPDATA_PROJECTS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    pathname === "/projects";
  const shouldUseOrderingQueueServerPaging =
    ORDERING_QUEUE_SERVER_PAGING_ENABLED &&
    isOrderingQueueServerPagedRoute(pathname);
  const shouldDeferOrderingQueueJobsHydration =
    !APPDATA_ORDERING_QUEUE_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseOrderingQueueServerPaging;
  const shouldDeferOrderingQueueOpportunityHydration =
    !APPDATA_ORDERING_QUEUE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseOrderingQueueServerPaging;
  const shouldDeferOrderingQueueAppDataHydration =
    shouldDeferOrderingQueueJobsHydration && shouldDeferOrderingQueueOpportunityHydration;
  const shouldUsePipelineServerPaging =
    PIPELINE_SERVER_PAGING_ENABLED &&
    isPipelineServerPagedRoute(pathname);
  const shouldDeferPipelineJobsHydration =
    shouldUsePipelineServerPaging;
  const shouldDeferPipelineOpportunityHydration =
    !APPDATA_PIPELINE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUsePipelineServerPaging;
  const shouldDeferPipelineAppDataHydration =
    shouldDeferPipelineJobsHydration && shouldDeferPipelineOpportunityHydration;
  const shouldUseGrowthChannelsServerPaging =
    GROWTH_CHANNELS_SERVER_PAGING_ENABLED &&
    isGrowthChannelsServerPagedRoute(pathname);
  const shouldDeferGrowthChannelsJobsHydration =
    !APPDATA_GROWTH_CHANNELS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseGrowthChannelsServerPaging;
  const shouldDeferGrowthChannelsOpportunityHydration =
    !APPDATA_GROWTH_CHANNELS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseGrowthChannelsServerPaging;
  const shouldDeferGrowthChannelsAppDataHydration =
    shouldDeferGrowthChannelsJobsHydration && shouldDeferGrowthChannelsOpportunityHydration;
  const shouldUseIntakeReviewServerPaging =
    INTAKE_REVIEW_SERVER_PAGING_ENABLED &&
    isIntakeReviewServerPagedRoute(pathname);
  const shouldDeferIntakeReviewJobsHydration =
    !APPDATA_INTAKE_REVIEW_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseIntakeReviewServerPaging;
  const shouldDeferIntakeReviewOpportunityHydration =
    !APPDATA_INTAKE_REVIEW_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseIntakeReviewServerPaging;
  const shouldDeferIntakeReviewAppDataHydration =
    shouldDeferIntakeReviewJobsHydration && shouldDeferIntakeReviewOpportunityHydration;
  const shouldUsePermittingServerPaging =
    PERMITTING_SERVER_PAGING_ENABLED &&
    isPermittingServerPagedRoute(pathname);
  const shouldDeferPermittingJobsHydration =
    !APPDATA_PERMITTING_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUsePermittingServerPaging;
  const shouldDeferPermittingOpportunityHydration =
    !APPDATA_PERMITTING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUsePermittingServerPaging;
  const shouldDeferPermittingAppDataHydration =
    shouldDeferPermittingJobsHydration && shouldDeferPermittingOpportunityHydration;
  const shouldUseInstallerOffersServerPaging =
    INSTALLER_OFFERS_SERVER_PAGING_ENABLED &&
    isInstallerOffersServerPagedRoute(pathname);
  const shouldDeferInstallerOffersJobsHydration =
    !APPDATA_INSTALLER_OFFERS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseInstallerOffersServerPaging;
  const shouldDeferInstallerOffersOpportunityHydration =
    !APPDATA_INSTALLER_OFFERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseInstallerOffersServerPaging;
  const shouldDeferInstallerOffersAppDataHydration =
    shouldDeferInstallerOffersJobsHydration && shouldDeferInstallerOffersOpportunityHydration;
  const shouldUseSchedulingDispatchServerPaging =
    SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED &&
    isSchedulingDispatchServerPagedRoute(pathname, search);
  const shouldDeferSchedulingDispatchJobsHydration =
    !APPDATA_SCHEDULING_DISPATCH_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseSchedulingDispatchServerPaging;
  const shouldDeferSchedulingDispatchOpportunityHydration =
    !APPDATA_SCHEDULING_DISPATCH_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseSchedulingDispatchServerPaging;
  const shouldDeferSchedulingDispatchAppDataHydration =
    shouldDeferSchedulingDispatchJobsHydration && shouldDeferSchedulingDispatchOpportunityHydration;
  const shouldUseExecutionServerPaging =
    EXECUTION_SERVER_PAGING_ENABLED &&
    isExecutionServerPagedRoute(pathname, search);
  const shouldDeferExecutionJobsHydration =
    !APPDATA_EXECUTION_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseExecutionServerPaging;
  const shouldDeferExecutionOpportunityHydration =
    !APPDATA_EXECUTION_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseExecutionServerPaging;
  const shouldDeferExecutionAppDataHydration =
    shouldDeferExecutionJobsHydration && shouldDeferExecutionOpportunityHydration;
  const shouldUseResolveServerPaging =
    RESOLVE_SERVER_PAGING_ENABLED &&
    isResolveServerPagedRoute(pathname, search);
  const shouldUseResolveServiceServerPaging =
    (RESOLVE_SERVICE_SERVER_PAGING_ENABLED &&
      isResolveServiceServerPagedRoute(pathname, search)) ||
    shouldUseResolveServerPaging;
  const shouldDeferResolveServiceJobsHydration =
    !APPDATA_RESOLVE_SERVICE_GLOBAL_JOBS_HYDRATION_ENABLED &&
    !APPDATA_RESOLVE_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseResolveServiceServerPaging;
  const shouldDeferResolveServiceOpportunityHydration =
    !APPDATA_RESOLVE_SERVICE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    !APPDATA_RESOLVE_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseResolveServiceServerPaging;
  const shouldDeferResolveServiceAppDataHydration =
    shouldDeferResolveServiceJobsHydration && shouldDeferResolveServiceOpportunityHydration;
  const shouldUseContractorsServerPaging =
    CONTRACTORS_SERVER_PAGING_ENABLED &&
    isContractorsServerPagedRoute(pathname);
  const shouldDeferContractorsJobsHydration =
    !APPDATA_CONTRACTORS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseContractorsServerPaging;
  const shouldDeferContractorsOpportunityHydration =
    !APPDATA_CONTRACTORS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseContractorsServerPaging;
  const shouldDeferContractorsAppDataHydration =
    shouldDeferContractorsJobsHydration && shouldDeferContractorsOpportunityHydration;
  const shouldUseContractorOnboardingServerPaging =
    CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED &&
    isContractorOnboardingServerPagedRoute(pathname);
  const shouldDeferContractorOnboardingJobsHydration =
    !APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseContractorOnboardingServerPaging;
  const shouldDeferContractorOnboardingOpportunityHydration =
    !APPDATA_CONTRACTOR_ONBOARDING_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseContractorOnboardingServerPaging;
  const shouldDeferContractorOnboardingAppDataHydration =
    shouldDeferContractorOnboardingJobsHydration && shouldDeferContractorOnboardingOpportunityHydration;
  const shouldUseCustomersServerPaging =
    CUSTOMERS_SERVER_PAGING_ENABLED &&
    isCustomersServerPagedRoute(pathname);
  const shouldDeferCustomersJobsHydration =
    !APPDATA_CUSTOMERS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseCustomersServerPaging;
  const shouldDeferCustomersOpportunityHydration =
    !APPDATA_CUSTOMERS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseCustomersServerPaging;
  const shouldDeferCustomersAppDataHydration =
    shouldDeferCustomersJobsHydration && shouldDeferCustomersOpportunityHydration;
  const shouldUseCommandDashboardServerPaging =
    COMMAND_DASHBOARD_SERVER_PAGING_ENABLED &&
    isCommandDashboardServerPagedRoute(pathname);
  const shouldDeferCommandDashboardJobsHydration =
    !APPDATA_COMMAND_DASHBOARD_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseCommandDashboardServerPaging;
  const shouldDeferCommandDashboardOpportunityHydration =
    !APPDATA_COMMAND_DASHBOARD_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseCommandDashboardServerPaging;
  const shouldDeferCommandDashboardAppDataHydration =
    shouldDeferCommandDashboardJobsHydration && shouldDeferCommandDashboardOpportunityHydration;
  const shouldUseReportsServerPaging =
    REPORTS_SERVER_PAGING_ENABLED &&
    isReportsServerPagedRoute(pathname);
  const shouldDeferReportsJobsHydration =
    !APPDATA_REPORTS_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseReportsServerPaging;
  const shouldDeferReportsOpportunityHydration =
    !APPDATA_REPORTS_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseReportsServerPaging;
  const shouldDeferReportsAppDataHydration =
    shouldDeferReportsJobsHydration && shouldDeferReportsOpportunityHydration;
  const shouldUseJobDetailServerQuery =
    JOB_DETAIL_SERVER_QUERY_ENABLED &&
    isJobDetailServerPagedRoute(pathname);
  const shouldDeferJobDetailJobsHydration =
    !APPDATA_JOB_DETAIL_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseJobDetailServerQuery;
  const shouldDeferJobDetailOpportunityHydration =
    !APPDATA_JOB_DETAIL_GLOBAL_OPPORTUNITY_HYDRATION_ENABLED &&
    shouldUseJobDetailServerQuery;
  const shouldDeferJobDetailAppDataHydration =
    shouldDeferJobDetailJobsHydration && shouldDeferJobDetailOpportunityHydration;
  const shouldUseWarehouseInventoryServerPaging =
    WAREHOUSE_SERVER_PAGING_ENABLED &&
    isWarehouseServerPagedRoute(pathname);
  const shouldDeferWarehouseJobsHydration =
    !APPDATA_WAREHOUSE_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseWarehouseInventoryServerPaging;
  const shouldDeferWarehouseOpportunityHydration =
    !APPDATA_WAREHOUSE_GLOBAL_JOBS_HYDRATION_ENABLED &&
    shouldUseWarehouseInventoryServerPaging;
  const shouldDeferWarehouseAppDataHydration =
    shouldDeferWarehouseJobsHydration && shouldDeferWarehouseOpportunityHydration;
  const shouldDeferRouteAppDataHydration =
    shouldDeferWarehouseAppDataHydration ||
    shouldDeferOrderingQueueAppDataHydration ||
    shouldDeferPipelineAppDataHydration ||
    shouldDeferGrowthChannelsAppDataHydration ||
    shouldDeferIntakeReviewAppDataHydration ||
    shouldDeferPermittingAppDataHydration ||
    shouldDeferInstallerOffersAppDataHydration ||
    shouldDeferSchedulingDispatchAppDataHydration ||
    shouldDeferExecutionAppDataHydration ||
    shouldDeferResolveServiceAppDataHydration ||
    shouldDeferContractorsAppDataHydration ||
    shouldDeferContractorOnboardingAppDataHydration ||
    shouldDeferCustomersAppDataHydration ||
    shouldDeferCommandDashboardAppDataHydration ||
    shouldDeferReportsAppDataHydration ||
    shouldDeferJobDetailAppDataHydration;

  /**
   * Normalize opportunities so legacy compat fields (customerName, createdBy,
   * customerPhone, customerEmail, customerAddress, status) are always populated
   * from the canonical fields. Many list views still read the deprecated names;
   * without this they render blank cells. Selectors and writers continue to
   * operate on the canonical fields — this is a read-time projection.
   */
  const normalizedOpps = useMemo<Opportunity[]>(
    () =>
      opps.map((o) => ({
        ...o,
        customerName: o.customerName ?? o.primaryName ?? "",
        customerPhone: o.customerPhone ?? o.primaryPhone ?? "",
        customerEmail: o.customerEmail ?? o.primaryEmail ?? "",
        customerAddress:
          o.customerAddress ??
          [o.address, o.city, [o.state, o.zip].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(", "),
        createdBy: o.createdBy ?? o.assignedRep ?? "",
        status: o.status ?? o.stage,
      })),
    [opps],
  );

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [stagingRecords, setStagingRecords] = useState<StagingRecord[]>([]);
  const [loadOutChecklists, setLoadOutChecklists] = useState<LoadOutChecklist[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading] = useState(false);
  const [commsMessages, setCommsMessages] = useState<Message[]>([]);
  const [serviceTickets, setServiceTickets] = useState<ServiceTicket[]>([]);
   const [serviceTicketsLoading, setServiceTicketsLoading] = useState(false);
  const [materialEvents, setMaterialEvents] = useState<MaterialEvent[]>([]);
  const [buildReviewEvents, setBuildReviewEvents] = useState<BuildReviewEvent[]>([]);
   const [buildReviewEventsLoading, setBuildReviewEventsLoading] = useState(false);
  const [jobMessages, setJobMessages] = useState<JobMessage[]>([]);
  const [jobMessagesLoading] = useState(false);
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
   const [leadSourcesLoading, setLeadSourcesLoading] = useState(false);
  const [followUpTasks, setFollowUpTasks] = useState<FollowUpTask[]>(validationSeed?.followUpTasks ?? []);
  const [installerWrapRecords, setInstallerWrapRecords] = useState<InstallerWrapRecord[]>([]);
  const [installFollowUpTasks, setInstallFollowUpTasks] = useState<InstallFollowUpTask[]>([]);
  const [appointmentResults, setAppointmentResults] = useState<AppointmentResult[]>([]);
  const [appointmentOutcomeExtensions, setAppointmentOutcomeExtensions] =
    useState<AppointmentOutcomeExtension[]>([]);
  const [consultationSessions, setConsultationSessions] = useState<ConsultationSessionData[]>([]);
   const [consultationSessionsLoading, setConsultationSessionsLoading] = useState(false);
  const [payProfiles, setPayProfiles] = useState<ContractorPayProfile[]>([]);
  const [specExecutions, setSpecExecutions] = useState<JobSpecExecution[]>([]);
  const [materialKits, setMaterialKits] = useState<MaterialKit[]>([]);
  const [specOutcomes, setSpecOutcomes] = useState<SpecExecutionOutcome[]>([]);
  const [marketplaceOffers, setMarketplaceOffers] = useState<MarketplaceOffer[]>([]);
  const [contractorInterests, setContractorInterests] = useState<ContractorInterest[]>([]);
  const [systemEvents, setSystemEvents] = useState<SystemEvent[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [contractorPortalSyncing, setContractorPortalSyncing] = useState(false);
  const [contractorPortalLastSavedAt, setContractorPortalLastSavedAt] = useState<string | null>(null);

  const CACHE_TTL_MS = {
    jobs: 30_000,
    issues: 30_000,
    contractors: 60_000,
    crews: 60_000,
    appointmentResults: 20_000,
    consultationSessions: 20_000,
    leads: 30_000,
    quotes: 30_000,
    leadSources: 5 * 60_000,
  } as const;

  const getResourceQueryKey = useCallback((key: string) => {
    switch (key) {
      case "jobs":
        return queryKeys.jobs();
      case "issues":
        return queryKeys.issues();
      case "contractors":
        return queryKeys.contractors();
      case "crews":
        return queryKeys.crews();
      case "appointment-results":
        return queryKeys.appointmentResults();
      case "quotes":
        return queryKeys.quotes();
      case "leads":
        return queryKeys.leads();
      case "lead-sources":
        return queryKeys.leadSources();
      case "consultation-sessions":
        return queryKeys.consultationSessions();
      default:
        return [key] as const;
    }
  }, []);

  const fetchWithCache = useCallback(
    async <T,>(
      key: string,
      ttlMs: number,
      fetcher: () => Promise<T>,
      force = false,
    ): Promise<T> => {
      // Compatibility default: keep React Query backed cache v2 enabled.
      // Flags exist to allow gradual rollout control without changing consumers.
      if (!DATA_CACHE_V2_ENABLED) {
        return fetcher();
      }
      const queryKey = getResourceQueryKey(key);
      const queryKeyString = JSON.stringify(queryKey);
      const existingState = queryClient.getQueryState(queryKey);
      const isFreshCacheHit = Boolean(
        existingState?.dataUpdatedAt &&
          Date.now() - existingState.dataUpdatedAt < ttlMs,
      );
      observeQueryStart(queryKeyString);
      if (force) {
        await queryClient.invalidateQueries({ queryKey, exact: true });
      }
      const startedAt = performance.now();
      let payloadBytes = 0;
      let queryFnExecuted = false;
      const queryOptions = {
        queryKey,
        queryFn: async () => {
          queryFnExecuted = true;
          const value = await fetcher();
          try {
            payloadBytes = new TextEncoder().encode(JSON.stringify(value ?? null)).length;
          } catch {
            payloadBytes = 0;
          }
          return value;
        },
        staleTime: ttlMs,
        gcTime: 10 * 60_000,
        retry: 1,
      } as const;

      // `strict` is reserved for tighter behavior later; today it remains wire-compatible.
      const fetchPromise = QUERY_MODE === "strict"
        ? queryClient.fetchQuery(queryOptions)
        : queryClient.fetchQuery(queryOptions);

      return fetchPromise.finally(() => {
        const durationMs = Math.round(performance.now() - startedAt);
        const cacheHit = !queryFnExecuted || isFreshCacheHit;
        recordQueryMetric({
          key: queryKeyString,
          durationMs,
          payloadBytes: queryFnExecuted ? payloadBytes : 0,
          cacheHit,
        });
      });
    },
    [getResourceQueryKey, queryClient],
  );

  const markContractorOnboardingComplete = useCallback(async (contractorId: string) => {
    const nowIso = new Date().toISOString();
    setContractors((prev) =>
      prev.map((c) =>
        c.id === contractorId
          ? ({ ...c, onboardingCompletedAt: nowIso } as Contractor)
          : c,
      ),
    );

    const { data: row } = await supabase
      .from("contractors")
      .select("metadata")
      .eq("id", contractorId)
      .maybeSingle();
    const metadata = row?.metadata && typeof row.metadata === "object"
      ? (row.metadata as Record<string, unknown>)
      : {};
    const nextMetadata: Record<string, unknown> = {
      ...metadata,
      onboardingCompletedAt: nowIso,
      onboarding_completed_at: nowIso,
    };
    const { error } = await supabase
      .from("contractors")
      .update({ metadata: nextMetadata })
      .eq("id", contractorId);
    if (error) {
      console.error("[AppDataContext] Failed to persist contractor onboarding completion:", error);
    }
  }, []);
  const refreshInFlightRef = useRef(0);
  const jobsRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobsRefreshInFlightRef = useRef(false);
  const jobsRefreshQueuedRef = useRef(false);
  const jobsSnapshotSigRef = useRef("");
  const jobsLastSyncedAtRef = useRef<string | null>(null);
  const latestRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRefreshQueuedRef = useRef(false);
  const latestRefreshLastStartedAtRef = useRef(0);
  const contractorSyncInFlightRef = useRef(0);
  const payProfilesLoadedRef = useRef(false);
  const specExecutionsLoadedRef = useRef(false);
  const materialKitsLoadedRef = useRef(false);
  const payProfileSigRef = useRef(new Map<string, string>());
  const specExecutionSigRef = useRef(new Map<string, string>());
  const materialKitSigRef = useRef(new Map<string, string>());
  const specBackfillAttemptedRef = useRef(new Set<string>());
  const specBackfillInFlightRef = useRef(false);

  const stableSerialize = useCallback((value: unknown) => JSON.stringify(value ?? null), []);
  const mergeMaterialKitsPreservingLocalEdits = useCallback(
    (dbKits: MaterialKit[], prevKits: MaterialKit[]): MaterialKit[] => {
      const dbByJobId = new Map(dbKits.map((kit) => [kit.jobId, kit]));
      const merged = [...dbKits];
      for (const localKit of prevKits) {
        const localSig = stableSerialize(localKit);
        const persistedSig = materialKitSigRef.current.get(localKit.jobId);
        const hasUnsavedLocalEdits = persistedSig !== localSig;
        if (!hasUnsavedLocalEdits) continue;
        const idx = merged.findIndex((kit) => kit.jobId === localKit.jobId);
        if (idx >= 0) {
          merged[idx] = localKit;
        } else if (!dbByJobId.has(localKit.jobId)) {
          merged.push(localKit);
        }
      }
      return merged;
    },
    [stableSerialize],
  );

  const mergeSpecExecutionsPreservingLocalEdits = useCallback(
    (dbExecutions: JobSpecExecution[], prevExecutions: JobSpecExecution[]): JobSpecExecution[] => {
      const dbByJobId = new Map(dbExecutions.map((execution) => [execution.jobId, execution]));
      const merged = [...dbExecutions];
      for (const localExecution of prevExecutions) {
        const localSig = stableSerialize(localExecution);
        const persistedSig = specExecutionSigRef.current.get(localExecution.jobId);
        const hasUnsavedLocalEdits = persistedSig !== localSig;
        if (!hasUnsavedLocalEdits) continue;
        const idx = merged.findIndex((execution) => execution.jobId === localExecution.jobId);
        if (idx >= 0) {
          merged[idx] = localExecution;
        } else if (!dbByJobId.has(localExecution.jobId)) {
          merged.push(localExecution);
        }
      }
      return merged;
    },
    [stableSerialize],
  );

  const beginContractorSync = useCallback(() => {
    contractorSyncInFlightRef.current += 1;
    setContractorPortalSyncing(true);
  }, []);

  const endContractorSync = useCallback((saved: boolean) => {
    contractorSyncInFlightRef.current = Math.max(0, contractorSyncInFlightRef.current - 1);
    if (saved) {
      setContractorPortalLastSavedAt(new Date().toISOString());
    }
    if (contractorSyncInFlightRef.current === 0) {
      setContractorPortalSyncing(false);
    }
  }, []);

  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const appendSystemEvents = useCallback((events: SystemEvent[]) => {
    if (!events.length) return;
    setSystemEvents((prev) => [...prev, ...events]);
    // Persist to Supabase — fire-and-forget, non-blocking
    void (async () => {
      try {
        const orgId = await getCurrentOrganizationId();
        const rows = events.map((e) => ({
          organization_id: orgId,
          event_type: e.type,
          job_id: uuidRe.test(e.jobId ?? "") ? e.jobId : null,
          contractor_id: uuidRe.test((e.payload?.contractorId as string) ?? "")
            ? (e.payload!.contractorId as string)
            : null,
          triggered_by: (e.payload?.triggeredBy as string | undefined) ?? null,
          payload: e.payload ?? {},
          created_at: e.timestamp,
        }));
        const { error } = await supabase.from("system_events").insert(rows);
        if (error) console.warn("Failed to persist system events to Supabase:", error);
      } catch {
        // Non-critical — swallow auth errors
      }
    })();
  }, []);

const isUuidLike = (val: string | undefined | null): val is string => {
  if (!val) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
};

const inferSpecArchetype = (projectType: string | undefined): SpecArchetype | undefined => {
  const p = (projectType ?? "").toLowerCase();
  if (p.includes("tub to shower") || p.includes("tub-to-shower")) return "TTS-STD";
  if (p.includes("shower replacement")) return "SHWR-RPL";
  if (p.includes("walk-in tub") || p.includes("walk in tub")) return "WALK-IN-TUB";
  if (p.includes("full bathroom") || p.includes("full bath")) return "FULL-BATH";
  if (p.includes("tub replacement")) return "TUB-RPL";
  if (p.includes("floor")) return "FLOORING";
  return undefined;
};



  const refreshJobsOnly = useCallback(async () => {
    if (validationMode) return;
    if (jobsRefreshInFlightRef.current) {
      jobsRefreshQueuedRef.current = true;
      return;
    }
    jobsRefreshInFlightRef.current = true;
    try {
      const syncCursor = jobsLastSyncedAtRef.current;
      const jobsRes = syncCursor
        ? await JobService.getUpdatedSince(syncCursor)
        : await fetchWithCache("jobs", CACHE_TTL_MS.jobs, () => JobService.getAll(), true);
      if (jobsRes.error) {
        throw jobsRes.error;
      }
      setLastRefreshedAt(new Date().toISOString());

      let nextJobs = jobsRes.data ?? [];
      const salesRepUuids = nextJobs
        .map((j) => j.salesRep)
        .filter((s): s is string => !!s && isUuidLike(s));
      if (salesRepUuids.length > 0) {
        const nameMap = await fetchProfileDisplayNames([...new Set(salesRepUuids)]);
        nextJobs = nextJobs.map((j) =>
          isUuidLike(j.salesRep) && nameMap.has(j.salesRep)
            ? { ...j, salesRep: nameMap.get(j.salesRep)! }
            : j,
        );
      }

      const nextCursor = getMaxJobUpdatedAt(nextJobs);
      if (nextCursor) {
        jobsLastSyncedAtRef.current = nextCursor;
      }

      if (!syncCursor) {
        const nextSig = stableSerialize(nextJobs);
        jobsLastSyncedAtRef.current = nextCursor;
        if (jobsSnapshotSigRef.current !== nextSig) {
          jobsSnapshotSigRef.current = nextSig;
          setJobs(nextJobs);
        }
        return;
      }

      if (nextJobs.length === 0) return;

      setJobs((prev) => {
        const byId = new Map(prev.map((job) => [job.id, job]));
        for (const changed of nextJobs) {
          byId.set(changed.id, { ...byId.get(changed.id), ...changed });
        }
        const merged = sortJobsByCreatedDesc(Array.from(byId.values()));
        const nextSig = stableSerialize(merged);
        if (jobsSnapshotSigRef.current === nextSig) return prev;
        jobsSnapshotSigRef.current = nextSig;
        return merged;
      });
    } catch (error) {
      console.warn("Failed to refresh jobs incrementally; falling back to full jobs refresh:", error);
      try {
        const jobsRes = await fetchWithCache("jobs", CACHE_TTL_MS.jobs, () => JobService.getAll(), true);
        let nextJobs = jobsRes.data ?? [];
        const salesRepUuids = nextJobs
          .map((j) => j.salesRep)
          .filter((s): s is string => !!s && isUuidLike(s));
        if (salesRepUuids.length > 0) {
          const nameMap = await fetchProfileDisplayNames([...new Set(salesRepUuids)]);
          nextJobs = nextJobs.map((j) =>
            isUuidLike(j.salesRep) && nameMap.has(j.salesRep)
              ? { ...j, salesRep: nameMap.get(j.salesRep)! }
              : j,
          );
        }
        const nextCursor = getMaxJobUpdatedAt(nextJobs);
        if (nextCursor) jobsLastSyncedAtRef.current = nextCursor;
        setLastRefreshedAt(new Date().toISOString());
        const nextSig = stableSerialize(nextJobs);
        if (jobsSnapshotSigRef.current !== nextSig) {
          jobsSnapshotSigRef.current = nextSig;
          setJobs(nextJobs);
        }
      } catch (fallbackError) {
        console.warn("Failed to refresh jobs from realtime signal:", fallbackError);
      }
    } finally {
      jobsRefreshInFlightRef.current = false;
      if (jobsRefreshQueuedRef.current && !validationMode) {
        jobsRefreshQueuedRef.current = false;
        if (jobsRefreshTimerRef.current) clearTimeout(jobsRefreshTimerRef.current);
        jobsRefreshTimerRef.current = setTimeout(() => {
          jobsRefreshTimerRef.current = null;
          void refreshJobsOnly();
        }, JOBS_REALTIME_REFRESH_DEBOUNCE_MS);
      }
    }
  }, [validationMode, fetchWithCache, stableSerialize]);

  useEffect(() => {
    if (
      validationMode ||
      !APPDATA_SPEC_BACKFILL_ON_LOAD_ENABLED ||
      jobs.length === 0 ||
      specBackfillInFlightRef.current
    ) return;
    const missing = jobs.filter(
      (job) => !job.boundJobSpec && !isExternalManagedImport(job) && !specBackfillAttemptedRef.current.has(job.id),
    );
    if (missing.length === 0) return;

    specBackfillInFlightRef.current = true;
    let cancelled = false;

    const runBackfill = async () => {
      try {
        const patchedById = new Map<string, Job["boundJobSpec"]>();
        for (const job of missing) {
          specBackfillAttemptedRef.current.add(job.id);
          const boundJobSpec = deriveBoundSpecForJob(job);
          if (!boundJobSpec) continue;
          const { error } = await JobService.update(job.id, { boundJobSpec });
          if (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.warn(`[SpecBackfill] Failed to persist boundJobSpec for ${job.id}:`, errorMessage);
            continue;
          }
          patchedById.set(job.id, boundJobSpec);
        }

        if (!cancelled && patchedById.size > 0) {
          setJobs((prev) => {
            let changed = false;
            const next = prev.map((j) => {
              const boundJobSpec = patchedById.get(j.id);
              if (!boundJobSpec) return j;
              changed = true;
              return { ...j, boundJobSpec };
            });
            return changed ? next : prev;
          });
        }
      } finally {
        specBackfillInFlightRef.current = false;
      }
    };

    void runBackfill();
    return () => {
      cancelled = true;
    };
  }, [jobs, validationMode]);

// Core DB hydration - split into critical (jobs, issues) and secondary batches
  useEffect(() => {
    if (validationMode) return;
    let cancelled = false;

    // ── Critical path: jobs + issues (needed for dashboard) ───────────────────
    const loadCritical = async () => {
      try {
        let [jobsRes, issuesRes] = await Promise.all([
          shouldDeferProjectsJobsHydration || shouldDeferWarehouseJobsHydration || shouldDeferOrderingQueueJobsHydration || shouldDeferPipelineJobsHydration || shouldDeferGrowthChannelsJobsHydration || shouldDeferIntakeReviewJobsHydration || shouldDeferPermittingJobsHydration || shouldDeferInstallerOffersJobsHydration || shouldDeferSchedulingDispatchJobsHydration || shouldDeferExecutionJobsHydration || shouldDeferResolveServiceJobsHydration || shouldDeferContractorsJobsHydration || shouldDeferContractorOnboardingJobsHydration || shouldDeferCustomersJobsHydration || shouldDeferCommandDashboardJobsHydration || shouldDeferReportsJobsHydration || shouldDeferJobDetailJobsHydration
            ? Promise.resolve({ data: [] as Job[], error: null })
            : fetchWithCache("jobs", CACHE_TTL_MS.jobs, () => JobService.getAll()),
          shouldDeferRouteAppDataHydration
            ? Promise.resolve({ data: [] as Issue[], error: null })
            : fetchWithCache("issues", CACHE_TTL_MS.issues, () => IssueService.getAll({ resolvePhotos: false })),
        ]);
        // Self-heal path: if first pass returned an error/null payload, invalidate and retry once.
        const jobsInvalid = !jobsRes || jobsRes.error || !Array.isArray(jobsRes.data);
        const issuesInvalid = !issuesRes || issuesRes.error || !Array.isArray(issuesRes.data);
        if (jobsInvalid || issuesInvalid) {
          const [jobsRetry, issuesRetry] = await Promise.all([
            jobsInvalid
              ? fetchWithCache("jobs", CACHE_TTL_MS.jobs, () => JobService.getAll(), true)
              : Promise.resolve(jobsRes),
            issuesInvalid
              ? fetchWithCache("issues", CACHE_TTL_MS.issues, () => IssueService.getAll({ resolvePhotos: false }), true)
              : Promise.resolve(issuesRes),
          ]);
          jobsRes = jobsRetry;
          issuesRes = issuesRetry;
        }
        if (cancelled) return;

        // Resolve salesRep UUIDs to names
        let jobs = jobsRes.data ?? [];
        const salesRepUuids = jobs
          .map(j => j.salesRep)
          .filter((s): s is string => !!s && isUuidLike(s));

        if (salesRepUuids.length > 0) {
          const uniqueUuids = [...new Set(salesRepUuids)];
          const nameMap = await fetchProfileDisplayNames(uniqueUuids);
          jobs = jobs.map((j) =>
            isUuidLike(j.salesRep) && nameMap.has(j.salesRep)
              ? { ...j, salesRep: nameMap.get(j.salesRep)! }
              : j,
          );
        }

        jobsSnapshotSigRef.current = stableSerialize(jobs);
        const jobsCursor = getMaxJobUpdatedAt(jobs);
        if (jobsCursor) jobsLastSyncedAtRef.current = jobsCursor;
        setJobs(jobs);
        setIssues(issuesRes.data ?? []);
        setLastRefreshedAt(new Date().toISOString());
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load critical AppDataContext resources:", error);
        }
      } finally {
        if (!cancelled) {
          setJobsLoading(false);
          setIssuesLoading(false);
        }
      }
    };

    // ── Secondary path: remaining resources (deferred) ────────────────────────
    const loadSecondary = async () => {
      try {
        // Batch secondary loads to avoid a 15-query parallel storm that
        // overwhelms the local Postgres connection pool.
        const [
          contractorsRes,
          crewsRes,
          commsRes,
          creditsRes,
          ticketsRes,
        ] = await Promise.all([
          fetchWithCache("contractors", CACHE_TTL_MS.contractors, () => ContractorService.fetchContractors()),
          fetchWithCache("crews", CACHE_TTL_MS.crews, async () =>
            await supabase
            .from("crews")
            .select("id, contractor_id, name, lead_installer_name, members, active"),
          ),
          CustomerCommService.fetchCustomerComms(),
          CreditService.getAll(),
          ServiceTicketService.getAll(),
        ]);
        if (cancelled) return;

        const [
          buildReviewRes,
          followUpsRes,
          apptResultsRes,
          sessionsRes,
          schedulingEventsRes,
        ] = await Promise.all([
          BuildReviewService.getAll(),
          FollowUpTaskService.getAll(),
          shouldDeferAppointmentsOpportunityHydration || shouldDeferProjectsOpportunityHydration || shouldDeferPipelineOpportunityHydration || shouldDeferGrowthChannelsOpportunityHydration || shouldDeferIntakeReviewOpportunityHydration || shouldDeferPermittingOpportunityHydration || shouldDeferInstallerOffersOpportunityHydration || shouldDeferSchedulingDispatchOpportunityHydration || shouldDeferExecutionOpportunityHydration || shouldDeferResolveServiceOpportunityHydration || shouldDeferContractorsOpportunityHydration || shouldDeferContractorOnboardingOpportunityHydration || shouldDeferCommandDashboardOpportunityHydration || shouldDeferReportsOpportunityHydration || shouldDeferJobDetailOpportunityHydration
            ? Promise.resolve({ data: [] as DbAppointmentResult[], error: null })
            : fetchWithCache("appointment-results", CACHE_TTL_MS.appointmentResults, () => AppointmentResultService.getAll()),
          fetchWithCache("consultation-sessions", CACHE_TTL_MS.consultationSessions, () => ConsultationService.getInProgressSessionSummaries()),
          JobOperationsService.listSchedulingEvents(),
        ]);
        if (cancelled) return;

        const [
          callbacksRes,
          holdbackLedgerRes,
          disputeEventsRes,
          softSuggestionsRes,
          overrideOutcomesRes,
        ] = await Promise.all([
          CallbackService.getAll(),
          HoldbackLedgerService.getAll(),
          DisputeEventService.getAll(),
          SoftSuggestionService.getAll(),
          OverrideOutcomeService.getAll(),
        ]);
        if (cancelled) return;

        setContractors(contractorsRes ?? []);
        if (crewsRes.error) {
          console.warn("Failed to load crews from Supabase:", crewsRes.error);
          setCrews([]);
        } else {
          setCrews(((crewsRes.data ?? []) as DbCrewRow[]).map(mapCrewRow));
        }
        setCustomerComms(commsRes ?? []);
        setCredits(creditsRes.data ?? []);
        setServiceTickets(ticketsRes.data ?? []);
        setBuildReviewEvents(buildReviewRes.data ?? []);
        setFollowUpTasks(followUpsRes.data ?? []);
        setConsultationSessions(sessionsRes.data ?? []);
        setCallbacks(callbacksRes.data ?? []);
        setHoldbackLedger(holdbackLedgerRes.data ?? []);
        setDisputeEvents(disputeEventsRes.data ?? []);
        setSoftSuggestions(softSuggestionsRes.data ?? []);
        setOverrideOutcomes(overrideOutcomesRes.data ?? []);
        setLastRefreshedAt(new Date().toISOString());

        const mappedResults = (apptResultsRes.data ?? [])
          .map(dbAppointmentResultToAppointmentResult)
          .filter((r): r is AppointmentResult => !!r);
        setAppointmentResults(mappedResults);

        // Reconcile missing schedule/assignee details from scheduling_events.
        // This restores records that were left in limbo by partial writes.
        const contractorNameById = new Map(
          (contractorsRes ?? []).map((c) => [c.id, c.companyName] as const),
        );
        const latestEventByJobId = new Map<string, SchedulingEventRow>();
        for (const evt of schedulingEventsRes) {
          const jobId = String(evt.job_id ?? "").trim();
          if (!jobId) continue;
          const prev = latestEventByJobId.get(jobId);
          if (!prev) {
            latestEventByJobId.set(jobId, evt);
            continue;
          }
          const prevTs = Date.parse(prev.updated_at ?? prev.start_at ?? prev.created_at ?? "");
          const nextTs = Date.parse(evt.updated_at ?? evt.start_at ?? evt.created_at ?? "");
          if (!Number.isNaN(nextTs) && (Number.isNaN(prevTs) || nextTs >= prevTs)) {
            latestEventByJobId.set(jobId, evt);
          }
        }

        if (latestEventByJobId.size > 0) {
          setJobs((prevJobs) =>
            prevJobs.map((job) => {
              const evt = latestEventByJobId.get(job.id);
              if (!evt) return job;

              const evtStatus = String(evt.status ?? "").toLowerCase();
              if (evtStatus === "cancelled" || evtStatus === "canceled") return job;

              const hasSchedule = Boolean(job.scheduledInstallDate || job.scheduledStartTime || job.scheduledEndTime);
              const hasAssignee = Boolean(
                String(job.assignedCrew ?? "").trim() ||
                String(job.assignedSubcontractor ?? "").trim(),
              );
              if (hasSchedule && hasAssignee) return job;

              const startAt = String(evt.start_at ?? "").trim();
              const endAt = String(evt.end_at ?? "").trim();
              const contractorId = String(evt.contractor_id ?? "").trim();
              const contractorName = contractorId ? (contractorNameById.get(contractorId) ?? "") : "";
              const assignmentType: Job["assignmentType"] =
                contractorName.length > 0 ? "Subcontractor" : job.assignmentType;

              return {
                ...job,
                scheduledInstallDate: startAt ? startAt.slice(0, 10) : job.scheduledInstallDate,
                scheduledStartTime: startAt || job.scheduledStartTime,
                scheduledEndTime: endAt || job.scheduledEndTime,
                assignmentType,
                assignedSubcontractor: contractorName || job.assignedSubcontractor,
                assignedCrew:
                  assignmentType === "Subcontractor"
                    ? (contractorName || job.assignedCrew)
                    : job.assignedCrew,
                dispatchStatus:
                  (startAt || contractorName) && job.assignmentType === "Unassigned"
                    ? "Assigned"
                    : job.dispatchStatus,
              };
            }),
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("Failed to load secondary AppDataContext resources:", error);
        }
      } finally {
        if (!cancelled) {
          setContractorsLoading(false);
          setCustomerCommsLoading(false);
          setCreditsLoading(false);
          setServiceTicketsLoading(false);
          setBuildReviewEventsLoading(false);
          setConsultationSessionsLoading(false);
        }
      }
    };

    void loadCritical();

    if (shouldDeferRouteAppDataHydration) {
      return () => {
        cancelled = true;
      };
    }

    // Defer secondary until after critical paint (next tick)
    const timer = setTimeout(() => {
      void loadSecondary();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [validationMode, fetchWithCache, shouldDeferAppointmentsOpportunityHydration, shouldDeferCommandDashboardJobsHydration, shouldDeferCommandDashboardOpportunityHydration, shouldDeferContractorOnboardingJobsHydration, shouldDeferContractorOnboardingOpportunityHydration, shouldDeferContractorsJobsHydration, shouldDeferContractorsOpportunityHydration, shouldDeferCustomersJobsHydration, shouldDeferCustomersOpportunityHydration, shouldDeferGrowthChannelsJobsHydration, shouldDeferGrowthChannelsOpportunityHydration, shouldDeferInstallerOffersJobsHydration, shouldDeferInstallerOffersOpportunityHydration, shouldDeferJobDetailJobsHydration, shouldDeferJobDetailOpportunityHydration, shouldDeferReportsJobsHydration, shouldDeferReportsOpportunityHydration, shouldDeferSchedulingDispatchJobsHydration, shouldDeferSchedulingDispatchOpportunityHydration, shouldDeferIntakeReviewJobsHydration, shouldDeferIntakeReviewOpportunityHydration, shouldDeferPermittingJobsHydration, shouldDeferPermittingOpportunityHydration, shouldDeferOrderingQueueJobsHydration, shouldDeferPipelineJobsHydration, shouldDeferPipelineOpportunityHydration, shouldDeferProjectsJobsHydration, shouldDeferProjectsOpportunityHydration, shouldDeferRouteAppDataHydration, shouldDeferWarehouseJobsHydration, shouldDeferExecutionJobsHydration, shouldDeferExecutionOpportunityHydration, shouldDeferResolveServiceJobsHydration, shouldDeferResolveServiceOpportunityHydration]);

  // Keep jobs cache coherent after any DB write (ordering, scheduling, QA, etc.).
  // Debounced to avoid burst reloads when a workflow emits multiple updates.
  useEffect(() => {
    if (validationMode || shouldDeferProjectsJobsHydration || shouldDeferWarehouseJobsHydration || shouldDeferOrderingQueueJobsHydration || shouldDeferPipelineJobsHydration || shouldDeferGrowthChannelsJobsHydration || shouldDeferIntakeReviewJobsHydration || shouldDeferPermittingJobsHydration || shouldDeferInstallerOffersJobsHydration || shouldDeferSchedulingDispatchJobsHydration || shouldDeferExecutionJobsHydration || shouldDeferResolveServiceJobsHydration || shouldDeferContractorsJobsHydration || shouldDeferContractorOnboardingJobsHydration || shouldDeferCustomersJobsHydration || shouldDeferCommandDashboardJobsHydration || shouldDeferReportsJobsHydration || shouldDeferJobDetailJobsHydration) return;
    const scheduleRefresh = () => {
      recordRealtimeMetric({
        source: "appdata-jobs-sync",
        event: "full-refetch",
        table: "jobs",
        detail: SUPABASE_SYNC_DEBUG ? { debounceMs: JOBS_REALTIME_REFRESH_DEBOUNCE_MS } : undefined,
      });
      if (jobsRefreshTimerRef.current) clearTimeout(jobsRefreshTimerRef.current);
      jobsRefreshTimerRef.current = setTimeout(() => {
        jobsRefreshTimerRef.current = null;
        void refreshJobsOnly();
      }, JOBS_REALTIME_REFRESH_DEBOUNCE_MS);
    };

    const channel = supabase
      .channel("appdata-jobs-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "jobs" }, scheduleRefresh)
      .subscribe();

    return () => {
      if (jobsRefreshTimerRef.current) {
        clearTimeout(jobsRefreshTimerRef.current);
        jobsRefreshTimerRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [refreshJobsOnly, shouldDeferCommandDashboardJobsHydration, shouldDeferContractorOnboardingJobsHydration, shouldDeferContractorsJobsHydration, shouldDeferCustomersJobsHydration, shouldDeferGrowthChannelsJobsHydration, shouldDeferInstallerOffersJobsHydration, shouldDeferJobDetailJobsHydration, shouldDeferReportsJobsHydration, shouldDeferSchedulingDispatchJobsHydration, shouldDeferIntakeReviewJobsHydration, shouldDeferPermittingJobsHydration, shouldDeferOrderingQueueJobsHydration, shouldDeferPipelineJobsHydration, shouldDeferProjectsJobsHydration, shouldDeferWarehouseJobsHydration, shouldDeferExecutionJobsHydration, shouldDeferResolveServiceJobsHydration, validationMode]);

  // ── Contractor pay profiles + JobSpec execution/kit data ──
  useEffect(() => {
    if (validationMode || shouldDeferProjectsJobsHydration || shouldDeferWarehouseJobsHydration || shouldDeferOrderingQueueJobsHydration || shouldDeferPipelineJobsHydration || shouldDeferGrowthChannelsJobsHydration || shouldDeferIntakeReviewJobsHydration || shouldDeferPermittingJobsHydration || shouldDeferInstallerOffersJobsHydration || shouldDeferSchedulingDispatchJobsHydration || shouldDeferExecutionJobsHydration || shouldDeferResolveServiceJobsHydration || shouldDeferContractorsJobsHydration || shouldDeferContractorOnboardingJobsHydration || shouldDeferCustomersJobsHydration || shouldDeferCommandDashboardJobsHydration || shouldDeferReportsJobsHydration || shouldDeferJobDetailJobsHydration) return;
    let cancelled = false;

    const loadExecutionMetadata = async () => {
      const [contractorsMetaRes, jobsMetaRes, executionRootsRes, materialKitRootsRes] = await Promise.all([
        supabase.from("contractors").select("id, metadata"),
        supabase.from("jobs").select("id, metadata"),
        supabase.from("job_spec_executions").select("job_id, spec_ref, spec_version, metadata"),
        supabase.from("job_spec_material_kits").select("job_id, spec_ref, spec_version, metadata"),
      ]);

      if (cancelled) return;

      // Fetch checklist items scoped to known job IDs to avoid PostgREST's
      // default 1000-row limit truncating results across the full table.
      const jobIdBatch = (jobsMetaRes.data ?? []).map((r: { id: string }) => r.id);
      const checklistItemsRes = jobIdBatch.length > 0
        ? await supabase
            .from("job_checklist_items")
            .select("job_id, phase_key, item_index, checked")
            .in("job_id", jobIdBatch)
        : { data: [] as { job_id: string; phase_key: string; item_index: number; checked: boolean }[], error: null };

      if (cancelled) return;

      if (contractorsMetaRes.error) {
        console.warn("Failed to load contractor metadata for pay profiles:", contractorsMetaRes.error);
        setPayProfiles([]);
        payProfilesLoadedRef.current = true;
      } else {
        const mapped = ((contractorsMetaRes.data ?? []) as DbContractorMetadataRow[])
          .map(mapPayProfileFromContractorMetadata)
          .filter((v): v is ContractorPayProfile => Boolean(v));
        payProfileSigRef.current = new Map(
          mapped.map((p) => [p.contractorId, stableSerialize(p)]),
        );
        setPayProfiles(mapped);
        payProfilesLoadedRef.current = true;
      }

      const rows = jobsMetaRes.error ? [] : ((jobsMetaRes.data ?? []) as DbJobMetadataRow[]);
      if (jobsMetaRes.error) {
        console.warn("Failed to load job metadata for spec execution/material kits:", jobsMetaRes.error);
      }
      // Build checklist map from job_checklist_items table (portal's source of truth).
      const checklistByJob1 = new Map<string, Record<string, boolean>>();
      if (!checklistItemsRes.error && checklistItemsRes.data) {
        for (const r of checklistItemsRes.data as { job_id: string; phase_key: string; item_index: number; checked: boolean }[]) {
          if (!checklistByJob1.has(r.job_id)) checklistByJob1.set(r.job_id, {});
          checklistByJob1.get(r.job_id)![`${r.phase_key}:${r.item_index}`] = r.checked;
        }
      } else if (checklistItemsRes.error) {
        console.warn("Failed to load job_checklist_items:", checklistItemsRes.error);
      }
      const metadataExecs = rows
        .map(mapSpecExecutionFromJobMetadata)
        .filter((v): v is JobSpecExecution => Boolean(v));
      const tableExecs = executionRootsRes.error
        ? []
        : ((executionRootsRes.data ?? []) as DbJobSpecExecutionRow[])
            .map(mapSpecExecutionFromExecutionRow)
            .filter((v): v is JobSpecExecution => Boolean(v));
      if (executionRootsRes.error) {
        console.warn("Failed to load job_spec_executions, falling back to jobs.metadata:", executionRootsRes.error);
      }
      const tableJobs = new Set(tableExecs.map((e) => e.jobId));
      const fallbackExecs = metadataExecs.filter((e) => !tableJobs.has(e.jobId));
      const baseExecMap = new Map<string, JobSpecExecution>(
        [...tableExecs, ...fallbackExecs].map((e) => [e.jobId, e]),
      );
      // Apply portal checklist to ALL jobs with checklist data — augments
      // not_started phases in existing executions AND synthesizes new ones.
      const execs: JobSpecExecution[] = [];
      const patchedIds = new Set<string>();
      for (const row of rows) {
        const meta = row.metadata ?? {};
        const bound = (meta.boundJobSpec ?? meta.bound_job_spec) as BoundJobSpec | undefined;
        const checklist = checklistByJob1.get(row.id);
        if (!checklist) continue;
        const existing = baseExecMap.get(row.id) ?? null;
        const result = bound
          ? applyPortalChecklistToExec(row.id, bound, checklist, existing)
          : synthesizeExecFromPortalChecklist(row.id, checklist, existing);
        if (result) { execs.push(result); patchedIds.add(row.id); }
      }
      // Include executions for jobs that had no portal checklist data.
      for (const e of [...tableExecs, ...fallbackExecs]) {
        if (!patchedIds.has(e.jobId)) execs.push(e);
      }
      const metadataKits = rows
        .map(mapMaterialKitFromJobMetadata)
        .filter((v): v is MaterialKit => Boolean(v));
      const tableKits = materialKitRootsRes.error
        ? []
        : ((materialKitRootsRes.data ?? []) as DbJobSpecMaterialKitRow[])
            .map(mapMaterialKitFromKitRow)
            .filter((v): v is MaterialKit => Boolean(v));
      if (materialKitRootsRes.error) {
        console.warn("Failed to load job_spec_material_kits, falling back to jobs.metadata:", materialKitRootsRes.error);
      }
      const tableKitJobs = new Set(tableKits.map((k) => k.jobId));
      const fallbackKits = metadataKits.filter((k) => !tableKitJobs.has(k.jobId));
      const kits = [...tableKits, ...fallbackKits];
      specExecutionSigRef.current = new Map(
        execs.map((e) => [e.jobId, stableSerialize(e)]),
      );
      materialKitSigRef.current = new Map(
        kits.map((k) => [k.jobId, stableSerialize(k)]),
      );
      setSpecExecutions((prev) => mergeSpecExecutionsPreservingLocalEdits(execs, prev));
      setMaterialKits((prev) => mergeMaterialKitsPreservingLocalEdits(kits, prev));
      specExecutionsLoadedRef.current = true;
      materialKitsLoadedRef.current = true;
    };

    void loadExecutionMetadata();
    return () => {
      cancelled = true;
    };
  }, [
    mergeMaterialKitsPreservingLocalEdits,
    mergeSpecExecutionsPreservingLocalEdits,
    shouldDeferCommandDashboardJobsHydration,
    shouldDeferContractorOnboardingJobsHydration,
    shouldDeferContractorsJobsHydration,
    shouldDeferCustomersJobsHydration,
    shouldDeferGrowthChannelsJobsHydration,
    shouldDeferIntakeReviewJobsHydration,
    shouldDeferInstallerOffersJobsHydration,
    shouldDeferJobDetailJobsHydration,
    shouldDeferReportsJobsHydration,
    shouldDeferSchedulingDispatchJobsHydration,
    shouldDeferPermittingJobsHydration,
    shouldDeferOrderingQueueJobsHydration,
    shouldDeferPipelineJobsHydration,
    shouldDeferProjectsJobsHydration,
    shouldDeferWarehouseJobsHydration,
    shouldDeferExecutionJobsHydration,
    shouldDeferResolveServiceJobsHydration,
    stableSerialize,
    validationMode,
  ]);

  // ── Contractor marketplace data (DB-backed) ──
  useEffect(() => {
    if (validationMode || shouldDeferRouteAppDataHydration) return;
    let cancelled = false;

    const loadMarketplaceData = async () => {
      const [offersRes, interestsRes, systemEventsRes] = await Promise.all([
        supabase
          .from("marketplace_offers")
          .select(
            "id, job_id, contractor_id, status, sent_at, sent_by, expires_at, payout_amount_snapshot, responded_at, response_reason, response_note, audit",
          )
          .order("sent_at", { ascending: false }),
        supabase
          .from("contractor_interests")
          .select("id, job_id, contractor_id, status, submitted_at, closed_at, close_reason")
          .order("submitted_at", { ascending: false }),
        supabase
          .from("system_events")
          .select("id, organization_id, event_type, job_id, lead_id, quote_id, contractor_id, triggered_by, payload, created_at")
          .order("created_at", { ascending: false })
          .limit(500),
      ]);

      if (cancelled) return;

      if (offersRes.error) {
        console.warn("Failed to load marketplace offers from Supabase:", offersRes.error);
        setMarketplaceOffers([]);
      } else {
        const mapped = ((offersRes.data ?? []) as DbMarketplaceOfferRow[])
          .map(mapMarketplaceOfferRow)
          .filter((v): v is MarketplaceOffer => Boolean(v));
        setMarketplaceOffers(mapped);
      }

      if (interestsRes.error) {
        console.warn("Failed to load contractor interests from Supabase:", interestsRes.error);
        setContractorInterests([]);
      } else {
        const mapped = ((interestsRes.data ?? []) as DbContractorInterestRow[])
          .map(mapContractorInterestRow)
          .filter((v): v is ContractorInterest => Boolean(v));
        setContractorInterests(mapped);
      }

      if (systemEventsRes.error) {
        console.warn("Failed to load system events from Supabase:", systemEventsRes.error);
        setSystemEvents([]);
      } else {
        const mapped = ((systemEventsRes.data ?? []) as DbSystemEventRow[])
          .map(mapSystemEventRow)
          .filter((v): v is SystemEvent => Boolean(v));
        setSystemEvents(mapped);
      }
    };

    void loadMarketplaceData();
    return () => {
      cancelled = true;
    };
  }, [shouldDeferRouteAppDataHydration, validationMode]);

  useEffect(() => {
    if (validationMode || shouldDeferRouteAppDataHydration) return;
    let cancelled = false;
    fetchWithCache("lead-sources", CACHE_TTL_MS.leadSources, () => LeadSourceService.getAll())
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("Failed to load lead sources from Supabase:", error);
          setLeadSources([]);
          return;
        }
        setLeadSources(data ?? []);
      })
      .finally(() => {
        if (!cancelled) setLeadSourcesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [validationMode, fetchWithCache, shouldDeferRouteAppDataHydration]);

  // Step 1 rewire: hydrate unified opportunity lifecycle from backend sources.
  // Keeps UI contract stable while moving lead/pipeline/quote flows to service-backed data.
  useEffect(() => {
    if (validationMode) return;
    if (
      shouldDeferAppointmentsOpportunityHydration ||
      shouldDeferProjectsOpportunityHydration ||
      shouldDeferWarehouseOpportunityHydration ||
      shouldDeferOrderingQueueOpportunityHydration ||
      shouldDeferPipelineOpportunityHydration ||
      shouldDeferGrowthChannelsOpportunityHydration ||
      shouldDeferIntakeReviewOpportunityHydration ||
      shouldDeferPermittingOpportunityHydration ||
      shouldDeferInstallerOffersOpportunityHydration ||
      shouldDeferSchedulingDispatchOpportunityHydration ||
      shouldDeferExecutionOpportunityHydration ||
      shouldDeferResolveServiceOpportunityHydration ||
      shouldDeferContractorsOpportunityHydration ||
      shouldDeferContractorOnboardingOpportunityHydration ||
      shouldDeferCommandDashboardOpportunityHydration ||
      shouldDeferReportsOpportunityHydration ||
      shouldDeferJobDetailOpportunityHydration
    ) {
      setSalesOpportunitiesLoading(false);
      return;
    }
    let cancelled = false;

    const loadOpportunities = async () => {
      setSalesOpportunitiesLoading(true);
      try {
        const [quotesRes, leadsRes, apptRes] = await Promise.all([
          fetchWithCache("quotes-opportunity-hydration", CACHE_TTL_MS.quotes, () => QuoteService.getOpportunityHydrationRows()),
          fetchWithCache("leads", CACHE_TTL_MS.leads, () => LeadService.getAll()),
          fetchWithCache("appointment-results", CACHE_TTL_MS.appointmentResults, () => AppointmentResultService.getAll()),
        ]);
        if (cancelled) return;

        const quoteRows = (quotesRes.data ?? []) as HydrationRow[];
        const leadRows = (leadsRes.data ?? []) as HydrationRow[];
        const apptRows = (apptRes.data ?? []) as DbAppointmentResult[];

      const latestApptByLeadId = new Map<string, string>();
      for (const row of apptRows) {
        const leadId = String(row.lead_id ?? "").trim();
        const scheduledAt = String(row.scheduled_at ?? "").trim();
        if (!leadId || !scheduledAt) continue;
        const cur = latestApptByLeadId.get(leadId);
        if (!cur || new Date(scheduledAt).getTime() > new Date(cur).getTime()) {
          latestApptByLeadId.set(leadId, scheduledAt);
        }
      }

      const assignedRepIds = Array.from(
        new Set(
          [...quoteRows, ...leadRows]
            .map((row) => row.assigned_rep)
            .filter((v): v is string => typeof v === "string" && v.trim().length > 0),
        ),
      );

      const repNameById = assignedRepIds.length > 0
        ? await fetchProfileDisplayNames(assignedRepIds)
        : new Map<string, string>();

      const quoteRowsWithRep: HydrationRow[] = quoteRows.map((row) => ({
        ...row,
        assigned_rep_name:
          typeof row.assigned_rep === "string"
            ? (repNameById.get(row.assigned_rep) ?? row.assigned_rep_name)
            : row.assigned_rep_name,
      }));

      const leadRowsWithRep: HydrationRow[] = leadRows.map((row) => ({
        ...row,
        assigned_rep_name:
          typeof row.assigned_rep === "string"
            ? (repNameById.get(row.assigned_rep) ?? row.assigned_rep_name)
            : row.assigned_rep_name,
      }));

      const leadById = new Map<string, HydrationRow>(
        leadRowsWithRep.map((l) => [String(l.id ?? ""), l]),
      );

      const fromQuotes = quoteRowsWithRep.map((q) => {
        const leadId = String(q.lead_id ?? "");
        const lead = leadById.get(leadId);
        return normalizeOpportunity({
          ...lead,
          ...q,
          // Keep quote identity fields authoritative
          id: q.id,
          quoteId: q.id,
          leadId: leadId || undefined,
          displayReference: q.displayReference ?? q.quote_number ?? q.id,
          // Prefer lead-side canonical contact/location/project when quote row is sparse
          primary_name: q.primary_name ?? lead?.primary_name,
          customer_name: q.customer_name ?? lead?.primary_name,
          address: q.address ?? lead?.address ?? lead?.address_line1,
          address_line1: q.address_line1 ?? lead?.address_line1 ?? lead?.address,
          city: q.city ?? lead?.city,
          state: q.state ?? lead?.state,
          zip: q.zip ?? q.postal_code ?? lead?.zip ?? lead?.postal_code,
          project_type: q.project_type ?? lead?.project_type,
          stage: toStage(q.stage ?? lead?.stage),
          appointment_time:
            q.appointment_time ??
            lead?.appointment_time ??
            (leadId ? latestApptByLeadId.get(leadId) : undefined),
          scheduled_at: leadId ? latestApptByLeadId.get(leadId) : undefined,
          assigned_rep_name: q.assigned_rep_name ?? lead?.assigned_rep_name,
          assigned_rep: q.assigned_rep ?? lead?.assigned_rep,
          // Lead is the write target for stage_history/activities — prefer it over quote row
          stage_history: (lead?.stage_history ?? q.stage_history) as any,
          activities: (lead?.activities ?? q.activities) as any,
        });
      });
      const quoteLeadIds = new Set(
        quoteRowsWithRep
          .map((q) => q.lead_id)
          .filter((v): v is string => typeof v === "string" && v.length > 0),
      );

      const fromLeads = leadRowsWithRep
        .filter((l) => !quoteLeadIds.has(String(l.id ?? "")))
        .map((l) =>
          normalizeOpportunity({
            id: String(l.id ?? `lead-${Date.now()}`),
            displayReference: formatLeadReference(String(l.id ?? "")),
            leadId: String(l.id ?? ""),
            primaryName: l.primary_name ?? [l.first_name, l.last_name].filter(Boolean).join(" "),
            secondaryName: l.secondary_name ?? "",
            primaryPhone: l.primary_phone ?? l.phone ?? "",
            secondaryPhone: l.secondary_phone ?? "",
            primaryEmail: l.primary_email ?? l.email ?? "",
            secondaryEmail: l.secondary_email ?? "",
            address: l.address ?? l.address_line1 ?? "",
            city: l.city ?? "",
            state: l.state ?? "",
            zip: l.zip ?? l.postal_code ?? "",
            projectType: String(l.project_type ?? DEFAULT_PROJECT_TYPE) as Opportunity["projectType"],
            leadSource: l.lead_source ?? l.source_name ?? "",
            stage: toStage(l.stage),
            appointment_time:
              l.appointment_time ?? latestApptByLeadId.get(String(l.id ?? "")),
            stageHistory: Array.isArray(l.stage_history) ? (l.stage_history as any[]) : [],
            assignedRep: l.assigned_rep_name ?? l.assigned_rep ?? "",
            ownershipHistory: [],
            activities: Array.isArray(l.activities) ? (l.activities as any[]) : [],
            totalEstimate: 0,
            createdAt: l.created_at ?? new Date().toISOString(),
            updatedAt: l.updated_at ?? l.created_at ?? new Date().toISOString(),
            notes: l.notes ?? "",
            customerId: l.customer_id ?? undefined,
            organizationId: l.organization_id ?? undefined,
          }),
        );

        setOpps([...fromQuotes, ...fromLeads]);
      } catch (error) {
        if (!cancelled) {
          console.warn("Failed to load sales opportunities from Supabase:", error);
          setOpps([]);
        }
      } finally {
        if (!cancelled) {
          setSalesOpportunitiesLoading(false);
        }
      }
    };

    void loadOpportunities();
    return () => {
      cancelled = true;
    };
  }, [validationMode, fetchWithCache, shouldDeferAppointmentsOpportunityHydration, shouldDeferCommandDashboardOpportunityHydration, shouldDeferContractorOnboardingOpportunityHydration, shouldDeferContractorsOpportunityHydration, shouldDeferGrowthChannelsOpportunityHydration, shouldDeferInstallerOffersOpportunityHydration, shouldDeferJobDetailOpportunityHydration, shouldDeferReportsOpportunityHydration, shouldDeferSchedulingDispatchOpportunityHydration, shouldDeferIntakeReviewOpportunityHydration, shouldDeferPermittingOpportunityHydration, shouldDeferOrderingQueueOpportunityHydration, shouldDeferPipelineOpportunityHydration, shouldDeferProjectsOpportunityHydration, shouldDeferWarehouseOpportunityHydration, shouldDeferExecutionOpportunityHydration, shouldDeferResolveServiceOpportunityHydration]);

  // ── SpecExecutionOutcome auto-derive ──
  // Recompute outcomes whenever execution / kit / job assignment state
  // changes. Pure derivation lives in `deriveOutcome`; this effect is the
  // ONLY writer of `specOutcomes`. Idempotent by construction — re-running
  // on identical inputs produces structurally identical records, so we
  // only commit a new array reference when something actually changed.
  useEffect(() => {
    setSpecOutcomes((prev) => {
      let next = prev;
      let mutated = false;
      for (const exec of specExecutions) {
        const job = jobs.find((j) => j.id === exec.jobId);
        if (!job?.boundJobSpec) continue;
        const kit = materialKits.find((k) => k.jobId === exec.jobId);
        const contractorKey = job.assignedSubcontractor || job.assignedCrew || "";
        const contractor = contractors.find(
          (c) => c.companyName === contractorKey || c.id === contractorKey,
        );
        const profile = contractor ? getPayProfile(payProfiles, contractor.id) : undefined;
        const derived = deriveOutcome({
          job,
          bound: job.boundJobSpec,
          execution: exec,
          kit,
          payProfileId: profile?.id,
        });
        if (!derived) continue;
        const existing = next.find((o) => o.jobId === derived.jobId);
        if (existing && JSON.stringify(existing) === JSON.stringify(derived)) continue;
        next = upsertOutcome(next, derived);
        mutated = true;
      }
      return mutated ? next : prev;
    });
  }, [jobs, specExecutions, materialKits, contractors, payProfiles]);

  /**
   * Hard-immutable add. Throws if a record with the same
   * appointmentResultId already exists. Outcomes are a system-of-record.
   */
  const addAppointmentOutcomeExtension = React.useCallback(
    (extension: AppointmentOutcomeExtension) => {
      setAppointmentOutcomeExtensions((prev) => {
        if (prev.some((e) => e.appointmentResultId === extension.appointmentResultId)) {
          throw new Error(
            `Appointment outcome already captured for ${extension.appointmentResultId}. ` +
              `Outcomes are immutable.`,
          );
        }
        return [...prev, extension];
      });
    },
    [],
  );

   // Explicit DB refresh trigger for page-entry freshness across operations pages.
   const refreshLatestData = useCallback(async () => {
     if (validationMode || refreshInFlightRef.current > 0) return;
     if (shouldDeferRouteAppDataHydration) {
       setLastRefreshedAt(new Date().toISOString());
       return;
     }
     refreshInFlightRef.current += 1;
     latestRefreshLastStartedAtRef.current = Date.now();
     setIsRefreshing(true);

     setJobsLoading(true);
     setIssuesLoading(true);
     setContractorsLoading(true);
     setCustomerCommsLoading(true);
     setCreditsLoading(true);
     setServiceTicketsLoading(true);
     setBuildReviewEventsLoading(true);
     setLeadSourcesLoading(true);
     setConsultationSessionsLoading(true);
     setSalesOpportunitiesLoading(true);

     try {
       const [
         jobsRes,
         issuesRes,
         contractorsRes,
         crewsRes,
         commsRes,
         creditsRes,
         ticketsRes,
         buildReviewRes,
         followUpsRes,
         apptResultsRes,
         leadSourcesRes,
         quotesRes,
         leadsRes,
         sessionsRes,
         marketplaceOffersRes,
         contractorInterestsRes,
         contractorsMetaRes,
         jobsMetaRes,
         executionRootsRes,
         materialKitRootsRes,
         systemEventsRefreshRes,
        ] = await Promise.all([
          fetchWithCache("jobs", CACHE_TTL_MS.jobs, () => JobService.getAll(), true),
          fetchWithCache("issues", CACHE_TTL_MS.issues, () => IssueService.getAll({ resolvePhotos: false }), true),
          fetchWithCache("contractors", CACHE_TTL_MS.contractors, () => ContractorService.fetchContractors(), true),
          supabase
            .from("crews")
            .select("id, contractor_id, name, lead_installer_name, members, active"),
          CustomerCommService.fetchCustomerComms(),
          CreditService.getAll(),
          ServiceTicketService.getAll(),
          BuildReviewService.getAll(),
          FollowUpTaskService.getAll(),
          fetchWithCache("appointment-results", CACHE_TTL_MS.appointmentResults, () => AppointmentResultService.getAll({ forceRefresh: true }), true), // apptResultsRes
          fetchWithCache("lead-sources", CACHE_TTL_MS.leadSources, () => LeadSourceService.getAll(), true),
          fetchWithCache("quotes-opportunity-hydration", CACHE_TTL_MS.quotes, () => QuoteService.getOpportunityHydrationRows({ forceRefresh: true }), true),
          fetchWithCache("leads", CACHE_TTL_MS.leads, () => LeadService.getAll({ forceRefresh: true }), true),
          fetchWithCache("consultation-sessions", CACHE_TTL_MS.consultationSessions, () => ConsultationService.getInProgressSessionSummaries(), true),
          supabase
            .from("marketplace_offers")
            .select(
              "id, job_id, contractor_id, status, sent_at, sent_by, expires_at, payout_amount_snapshot, responded_at, response_reason, response_note, audit",
            )
            .order("sent_at", { ascending: false }),
          supabase
            .from("contractor_interests")
            .select("id, job_id, contractor_id, status, submitted_at, closed_at, close_reason")
            .order("submitted_at", { ascending: false }),
          supabase.from("contractors").select("id, metadata"),
          supabase.from("jobs").select("id, metadata"),
          supabase.from("job_spec_executions").select("job_id, spec_ref, spec_version, metadata"),
          supabase.from("job_spec_material_kits").select("job_id, spec_ref, spec_version, metadata"),
          supabase
            .from("system_events")
            .select("id, organization_id, event_type, job_id, lead_id, quote_id, contractor_id, triggered_by, payload, created_at")
            .order("created_at", { ascending: false })
            .limit(500),
        ]);

       // Fetch checklist items scoped to known job IDs to avoid the 1000-row limit.
       const jobIdBatch2 = (jobsMetaRes.data ?? []).map((r: { id: string }) => r.id);
       const checklistItemsRes2 = jobIdBatch2.length > 0
         ? await supabase
             .from("job_checklist_items")
             .select("job_id, phase_key, item_index, checked")
             .in("job_id", jobIdBatch2)
         : { data: [] as { job_id: string; phase_key: string; item_index: number; checked: boolean }[], error: null };

       const refreshedJobs = jobsRes.data ?? [];
       const refreshedJobsCursor = getMaxJobUpdatedAt(refreshedJobs);
       if (refreshedJobsCursor) jobsLastSyncedAtRef.current = refreshedJobsCursor;
       const refreshedJobsSig = stableSerialize(refreshedJobs);
       if (jobsSnapshotSigRef.current !== refreshedJobsSig) {
         jobsSnapshotSigRef.current = refreshedJobsSig;
         setJobs(refreshedJobs);
       }
       setIssues(issuesRes.data ?? []);
       setContractors(contractorsRes ?? []);
       if (crewsRes.error) {
         console.warn("Failed to refresh crews from Supabase:", crewsRes.error);
         setCrews([]);
       } else {
         setCrews(((crewsRes.data ?? []) as DbCrewRow[]).map(mapCrewRow));
       }
       setCustomerComms(commsRes ?? []);
       setCredits(creditsRes.data ?? []);
       setServiceTickets(ticketsRes.data ?? []);
       setBuildReviewEvents(buildReviewRes.data ?? []);
       setFollowUpTasks(followUpsRes.data ?? []);
       setConsultationSessions(sessionsRes.data ?? []);

       const mappedResults = (apptResultsRes.data ?? [])
        .map(dbAppointmentResultToAppointmentResult)
        .filter((r): r is AppointmentResult => !!r);
      setAppointmentResults(mappedResults);

      if (marketplaceOffersRes.error) {
        console.warn("Failed to refresh marketplace offers from Supabase:", marketplaceOffersRes.error);
        setMarketplaceOffers([]);
      } else {
        const mapped = ((marketplaceOffersRes.data ?? []) as DbMarketplaceOfferRow[])
          .map(mapMarketplaceOfferRow)
          .filter((v): v is MarketplaceOffer => Boolean(v));
        setMarketplaceOffers(mapped);
      }

      if (contractorInterestsRes.error) {
        console.warn("Failed to refresh contractor interests from Supabase:", contractorInterestsRes.error);
        setContractorInterests([]);
      } else {
        const mapped = ((contractorInterestsRes.data ?? []) as DbContractorInterestRow[])
          .map(mapContractorInterestRow)
          .filter((v): v is ContractorInterest => Boolean(v));
        setContractorInterests(mapped);
      }

      if (systemEventsRefreshRes.error) {
        console.warn("Failed to refresh system events from Supabase:", systemEventsRefreshRes.error);
        setSystemEvents([]);
      } else {
        const mapped = ((systemEventsRefreshRes.data ?? []) as DbSystemEventRow[])
          .map(mapSystemEventRow)
          .filter((v): v is SystemEvent => Boolean(v));
        setSystemEvents(mapped);
      }

      if (contractorsMetaRes.error) {
        console.warn("Failed to refresh contractor metadata for pay profiles:", contractorsMetaRes.error);
        setPayProfiles([]);
        payProfilesLoadedRef.current = true;
      } else {
        const mapped = ((contractorsMetaRes.data ?? []) as DbContractorMetadataRow[])
          .map(mapPayProfileFromContractorMetadata)
          .filter((v): v is ContractorPayProfile => Boolean(v));
        payProfileSigRef.current = new Map(
          mapped.map((p) => [p.contractorId, stableSerialize(p)]),
        );
        setPayProfiles(mapped);
        payProfilesLoadedRef.current = true;
      }

      const rows = jobsMetaRes.error ? [] : ((jobsMetaRes.data ?? []) as DbJobMetadataRow[]);
      if (jobsMetaRes.error) {
        console.warn("Failed to refresh job metadata for spec executions/material kits:", jobsMetaRes.error);
      }
      const checklistByJob2 = new Map<string, Record<string, boolean>>();
      if (!checklistItemsRes2.error && checklistItemsRes2.data) {
        for (const r of checklistItemsRes2.data as { job_id: string; phase_key: string; item_index: number; checked: boolean }[]) {
          if (!checklistByJob2.has(r.job_id)) checklistByJob2.set(r.job_id, {});
          checklistByJob2.get(r.job_id)![`${r.phase_key}:${r.item_index}`] = r.checked;
        }
      } else if (checklistItemsRes2.error) {
        console.warn("Failed to refresh job_checklist_items:", checklistItemsRes2.error);
      }
      const metadataExecs = rows
        .map(mapSpecExecutionFromJobMetadata)
        .filter((v): v is JobSpecExecution => Boolean(v));
      const tableExecs = executionRootsRes.error
        ? []
        : ((executionRootsRes.data ?? []) as DbJobSpecExecutionRow[])
            .map(mapSpecExecutionFromExecutionRow)
            .filter((v): v is JobSpecExecution => Boolean(v));
      if (executionRootsRes.error) {
        console.warn("Failed to refresh job_spec_executions, falling back to jobs.metadata:", executionRootsRes.error);
      }
      const tableJobs = new Set(tableExecs.map((e) => e.jobId));
      const fallbackExecs = metadataExecs.filter((e) => !tableJobs.has(e.jobId));
      const baseExecMap2 = new Map<string, JobSpecExecution>(
        [...tableExecs, ...fallbackExecs].map((e) => [e.jobId, e]),
      );
      const execs: JobSpecExecution[] = [];
      const patchedIds2 = new Set<string>();
      for (const row of rows) {
        const meta = row.metadata ?? {};
        const bound = (meta.boundJobSpec ?? meta.bound_job_spec) as BoundJobSpec | undefined;
        const checklist = checklistByJob2.get(row.id);
        if (!checklist) continue;
        const existing2 = baseExecMap2.get(row.id) ?? null;
        const result = bound
          ? applyPortalChecklistToExec(row.id, bound, checklist, existing2)
          : synthesizeExecFromPortalChecklist(row.id, checklist, existing2);
        if (result) { execs.push(result); patchedIds2.add(row.id); }
      }
      for (const e of [...tableExecs, ...fallbackExecs]) {
        if (!patchedIds2.has(e.jobId)) execs.push(e);
      }
      const metadataKits = rows
        .map(mapMaterialKitFromJobMetadata)
        .filter((v): v is MaterialKit => Boolean(v));
      const tableKits = materialKitRootsRes.error
        ? []
        : ((materialKitRootsRes.data ?? []) as DbJobSpecMaterialKitRow[])
            .map(mapMaterialKitFromKitRow)
            .filter((v): v is MaterialKit => Boolean(v));
      if (materialKitRootsRes.error) {
        console.warn("Failed to refresh job_spec_material_kits, falling back to jobs.metadata:", materialKitRootsRes.error);
      }
      const tableKitJobs = new Set(tableKits.map((k) => k.jobId));
      const fallbackKits = metadataKits.filter((k) => !tableKitJobs.has(k.jobId));
      const kits = [...tableKits, ...fallbackKits];
      specExecutionSigRef.current = new Map(
        execs.map((e) => [e.jobId, stableSerialize(e)]),
      );
      materialKitSigRef.current = new Map(
        kits.map((k) => [k.jobId, stableSerialize(k)]),
      );
      setSpecExecutions((prev) => mergeSpecExecutionsPreservingLocalEdits(execs, prev));
      setMaterialKits((prev) => mergeMaterialKitsPreservingLocalEdits(kits, prev));
      specExecutionsLoadedRef.current = true;
      materialKitsLoadedRef.current = true;

      if (leadSourcesRes.error) {
        console.warn("Failed to refresh lead sources from Supabase:", leadSourcesRes.error);
        setLeadSources([]);
      } else {
        setLeadSources(leadSourcesRes.data ?? []);
      }

      const quoteRows = (quotesRes.data ?? []) as HydrationRow[];
      const leadRows = (leadsRes.data ?? []) as HydrationRow[];
      const apptRows = (apptResultsRes.data ?? []) as DbAppointmentResult[];

      const latestApptByLeadId = new Map<string, string>();
      for (const row of apptRows) {
        const leadId = String(row.lead_id ?? "").trim();
        const scheduledAt = String(row.scheduled_at ?? "").trim();
        if (!leadId || !scheduledAt) continue;
        const cur = latestApptByLeadId.get(leadId);
        if (!cur || new Date(scheduledAt).getTime() > new Date(cur).getTime()) {
          latestApptByLeadId.set(leadId, scheduledAt);
        }
      }

      const assignedRepIds = Array.from(
        new Set(
          [...quoteRows, ...leadRows]
            .map((row) => row.assigned_rep)
            .filter((v): v is string => typeof v === "string" && v.trim().length > 0),
        ),
      );

      const repNameById = assignedRepIds.length > 0
        ? await fetchProfileDisplayNames(assignedRepIds)
        : new Map<string, string>();

      const quoteRowsWithRep: HydrationRow[] = quoteRows.map((row) => ({
        ...row,
        assigned_rep_name:
          typeof row.assigned_rep === "string"
            ? (repNameById.get(row.assigned_rep) ?? row.assigned_rep_name)
            : row.assigned_rep_name,
      }));

      const leadRowsWithRep: HydrationRow[] = leadRows.map((row) => ({
        ...row,
        assigned_rep_name:
          typeof row.assigned_rep === "string"
            ? (repNameById.get(row.assigned_rep) ?? row.assigned_rep_name)
            : row.assigned_rep_name,
      }));

      const leadById = new Map<string, HydrationRow>(
        leadRowsWithRep.map((l) => [String(l.id ?? ""), l]),
      );

       const fromQuotes = quoteRowsWithRep.map((q) => {
         const leadId = String(q.lead_id ?? "");
         const lead = leadById.get(leadId);
         return normalizeOpportunity({
           ...lead,
           ...q,
           id: q.id,
           quoteId: q.id,
           leadId: leadId || undefined,
           displayReference: q.displayReference ?? q.quote_number ?? q.id,
           primary_name: q.primary_name ?? lead?.primary_name,
           customer_name: q.customer_name ?? lead?.primary_name,
           address: q.address ?? lead?.address ?? lead?.address_line1,
           address_line1: q.address_line1 ?? lead?.address_line1 ?? lead?.address,
           city: q.city ?? lead?.city,
           state: q.state ?? lead?.state,
           zip: q.zip ?? q.postal_code ?? lead?.zip ?? lead?.postal_code,
           project_type: q.project_type ?? lead?.project_type,
           stage: toStage(q.stage ?? lead?.stage),
           appointment_time:
             q.appointment_time ??
             lead?.appointment_time ??
             (leadId ? latestApptByLeadId.get(leadId) : undefined),
           scheduled_at: leadId ? latestApptByLeadId.get(leadId) : undefined,
           assigned_rep_name: q.assigned_rep_name ?? lead?.assigned_rep_name,
           assigned_rep: q.assigned_rep ?? lead?.assigned_rep,
           // Lead is the write target for stage_history/activities — prefer it over quote row
           stage_history: (lead?.stage_history ?? q.stage_history) as any,
           activities: (lead?.activities ?? q.activities) as any,
         } as any);
       });

      const quoteLeadIds = new Set(
        quoteRowsWithRep
          .map((q) => q.lead_id)
          .filter((v): v is string => typeof v === "string" && v.length > 0),
      );

      const fromLeads = leadRowsWithRep
        .filter((l) => !quoteLeadIds.has(String(l.id ?? "")))
        .map((l) =>
          normalizeOpportunity({
            id: String(l.id ?? `lead-${Date.now()}`),
            displayReference: formatLeadReference(String(l.id ?? "")),
            leadId: String(l.id ?? ""),
            primaryName: l.primary_name ?? [l.first_name, l.last_name].filter(Boolean).join(" "),
            secondaryName: l.secondary_name ?? "",
            primaryPhone: l.primary_phone ?? l.phone ?? "",
            secondaryPhone: l.secondary_phone ?? "",
            primaryEmail: l.primary_email ?? l.email ?? "",
            secondaryEmail: l.secondary_email ?? "",
            address: l.address ?? l.address_line1 ?? "",
            city: l.city ?? "",
            state: l.state ?? "",
            zip: l.zip ?? l.postal_code ?? "",
            projectType: String(l.project_type ?? DEFAULT_PROJECT_TYPE) as Opportunity["projectType"],
            leadSource: l.lead_source ?? l.source_name ?? "",
            stage: toStage(l.stage),
            appointment_time:
              l.appointment_time ?? latestApptByLeadId.get(String(l.id ?? "")),
            stageHistory: Array.isArray(l.stage_history) ? (l.stage_history as any[]) : [],
            assignedRep: l.assigned_rep_name ?? l.assigned_rep ?? "",
            ownershipHistory: [],
            activities: Array.isArray(l.activities) ? (l.activities as any[]) : [],
            totalEstimate: 0,
            createdAt: l.created_at ?? new Date().toISOString(),
            updatedAt: l.updated_at ?? l.created_at ?? new Date().toISOString(),
            notes: l.notes ?? "",
            customerId: l.customer_id ?? undefined,
            organizationId: l.organization_id ?? undefined,
          }),
        );

      setOpps([...fromQuotes, ...fromLeads]);
    } catch (error) {
      console.warn("Failed to refresh AppDataContext resources:", error);
     } finally {
       setJobsLoading(false);
       setIssuesLoading(false);
       setContractorsLoading(false);
       setCustomerCommsLoading(false);
       setCreditsLoading(false);
       setServiceTicketsLoading(false);
       setBuildReviewEventsLoading(false);
       setLeadSourcesLoading(false);
       setConsultationSessionsLoading(false);
       setSalesOpportunitiesLoading(false);
       setLastRefreshedAt(new Date().toISOString());
       refreshInFlightRef.current = Math.max(0, refreshInFlightRef.current - 1);
       if (refreshInFlightRef.current === 0) {
         setIsRefreshing(false);
       }
     }
  }, [stableSerialize, validationMode, fetchWithCache, shouldDeferRouteAppDataHydration]);

  const scheduleLatestDataRefresh = useCallback(
    (delayMs = FULL_REFRESH_BACKGROUND_DEBOUNCE_MS) => {
      if (validationMode || shouldDeferRouteAppDataHydration) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      latestRefreshQueuedRef.current = true;
      if (latestRefreshTimerRef.current) {
        clearTimeout(latestRefreshTimerRef.current);
      }

      const elapsedSinceLastStart = Date.now() - latestRefreshLastStartedAtRef.current;
      const cooldownDelay = Math.max(
        delayMs,
        FULL_REFRESH_BACKGROUND_COOLDOWN_MS - elapsedSinceLastStart,
      );

      latestRefreshTimerRef.current = setTimeout(() => {
        latestRefreshTimerRef.current = null;
        if (!latestRefreshQueuedRef.current) return;
        if (refreshInFlightRef.current > 0) {
          latestRefreshTimerRef.current = setTimeout(() => {
            latestRefreshTimerRef.current = null;
            if (!latestRefreshQueuedRef.current || refreshInFlightRef.current > 0) return;
            latestRefreshQueuedRef.current = false;
            void refreshLatestData();
          }, FULL_REFRESH_BACKGROUND_COOLDOWN_MS);
          return;
        }
        latestRefreshQueuedRef.current = false;
        void refreshLatestData();
      }, cooldownDelay);
    },
    [refreshLatestData, shouldDeferRouteAppDataHydration, validationMode],
  );

  const initialRefreshStartedRef = useRef(false);
  useEffect(() => {
    if (
      validationMode ||
      shouldDeferRouteAppDataHydration ||
      !APPDATA_INITIAL_FULL_REFRESH_ENABLED ||
      initialRefreshStartedRef.current
    ) return;
    initialRefreshStartedRef.current = true;
    void refreshLatestData();
  }, [refreshLatestData, shouldDeferRouteAppDataHydration, validationMode]);

  useEffect(
    () => () => {
      if (latestRefreshTimerRef.current) {
        clearTimeout(latestRefreshTimerRef.current);
        latestRefreshTimerRef.current = null;
      }
    },
    [],
  );

  // Multi-resource realtime coherence with targeted refresh.
  // We batch table changes and refresh only impacted slices to reduce rerenders.
  useEffect(() => {
    if (validationMode || shouldDeferRouteAppDataHydration) return;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const pendingTables = new Set<string>();
    const pendingPayloads = new Map<string, Array<{ eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> }>>();

    const scheduleRefresh = (table: string, payload?: { eventType?: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      recordRealtimeMetric({
        source: "appdata-shared-sync",
        event: ["quotes", "leads", "appointment_results"].includes(table) ? "full-refetch" : "exact-refetch",
        table,
        detail: SUPABASE_SYNC_DEBUG ? { pendingCount: pendingTables.size + 1 } : undefined,
      });
      pendingTables.add(table);
      if (payload) {
        const list = pendingPayloads.get(table) ?? [];
        list.push(payload);
        pendingPayloads.set(table, list);
      }
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        const tables = new Set(pendingTables);
        const payloads = new Map(pendingPayloads);
        pendingTables.clear();
        pendingPayloads.clear();
        refreshTimer = null;
        void (async () => {
          // Coupled opportunity surface still uses shared hydration shape.
          if (tables.has("quotes") || tables.has("leads") || tables.has("appointment_results")) {
            scheduleLatestDataRefresh();
            return;
          }

          let refreshedFromRealtime = false;
          if (tables.has("issues")) {
            const issuePayloads = payloads.get("issues") ?? [];
            let handledPayloads = issuePayloads.length > 0;
            for (const payload of issuePayloads) {
              const eventType = String(payload.eventType ?? "").toUpperCase();
              const dbId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
              const issueNumber = String((payload.new?.issue_number ?? payload.old?.issue_number ?? "") || "");
              if (eventType === "DELETE") {
                if (!dbId && !issueNumber) {
                  handledPayloads = false;
                  break;
                }
                setIssues((prev) => prev.filter((issue) => issue.id !== issueNumber && issue.id !== dbId));
                recordRealtimeMetric({ source: "appdata-shared-sync", event: "patch", table: "issues", entityId: issueNumber || dbId });
                continue;
              }
              if (!issueNumber) {
                handledPayloads = false;
                break;
              }
              const row = await IssueService.getById(issueNumber);
              if (row.error || !row.data) {
                handledPayloads = false;
                break;
              }
              setIssues((prev) => {
                const exists = prev.some((issue) => issue.id === row.data!.id);
                const next = exists
                  ? prev.map((issue) => issue.id === row.data!.id ? row.data! : issue)
                  : [row.data!, ...prev];
                return next;
              });
              recordRealtimeMetric({ source: "appdata-shared-sync", event: "exact-refetch", table: "issues", entityId: issueNumber });
            }
            if (!handledPayloads) {
              const res = await fetchWithCache("issues", CACHE_TTL_MS.issues, () => IssueService.getAll({ resolvePhotos: false }), true);
              setIssues(res.data ?? []);
            }
            refreshedFromRealtime = true;
          }
          if (tables.has("contractors")) {
            const contractorPayloads = payloads.get("contractors") ?? [];
            let handledPayloads = contractorPayloads.length > 0;
            for (const payload of contractorPayloads) {
              const eventType = String(payload.eventType ?? "").toUpperCase();
              const contractorId = String((payload.new?.id ?? payload.old?.id ?? "") || "");
              if (!contractorId) {
                handledPayloads = false;
                break;
              }
              if (eventType === "DELETE") {
                setContractors((prev) => prev.filter((contractor) => contractor.id !== contractorId));
                recordRealtimeMetric({ source: "appdata-shared-sync", event: "patch", table: "contractors", entityId: contractorId });
                continue;
              }
              const contractor = await ContractorService.fetchContractorById(contractorId);
              if (!contractor) {
                handledPayloads = false;
                break;
              }
              setContractors((prev) => {
                const exists = prev.some((item) => item.id === contractor.id);
                return exists
                  ? prev.map((item) => item.id === contractor.id ? contractor : item)
                  : [contractor, ...prev];
              });
              recordRealtimeMetric({ source: "appdata-shared-sync", event: "exact-refetch", table: "contractors", entityId: contractorId });
            }
            if (!handledPayloads) {
              const contractorsRes = await fetchWithCache("contractors", CACHE_TTL_MS.contractors, () => ContractorService.fetchContractors(), true);
              setContractors(contractorsRes ?? []);
            }
            refreshedFromRealtime = true;
          }
          if (tables.has("crews")) {
            const crewsRes = await supabase
              .from("crews")
              .select("id, contractor_id, name, lead_installer_name, members, active");
            if (crewsRes.error) {
              console.warn("Failed to refresh crews from realtime signal:", crewsRes.error);
              setCrews([]);
            } else {
              setCrews(((crewsRes.data ?? []) as DbCrewRow[]).map(mapCrewRow));
            }
            refreshedFromRealtime = true;
          }
          if (tables.has("follow_up_tasks")) {
            const res = await FollowUpTaskService.getAll();
            setFollowUpTasks(res.data ?? []);
            refreshedFromRealtime = true;
          }
          if (tables.has("service_tickets")) {
            const res = await ServiceTicketService.getAll();
            setServiceTickets(res.data ?? []);
            refreshedFromRealtime = true;
          }
          if (tables.has("customer_comms")) {
            const res = await CustomerCommService.fetchCustomerComms();
            setCustomerComms(res ?? []);
            refreshedFromRealtime = true;
          }
          if (tables.has("build_review_events")) {
            const res = await BuildReviewService.getAll();
            setBuildReviewEvents(res.data ?? []);
            refreshedFromRealtime = true;
          }
          if (tables.has("marketplace_offers") || tables.has("contractor_interests")) {
            const [offersRes, interestsRes] = await Promise.all([
              supabase
                .from("marketplace_offers")
                .select("id, job_id, contractor_id, status, sent_at, sent_by, expires_at, payout_amount_snapshot, responded_at, response_reason, response_note, audit")
                .order("sent_at", { ascending: false }),
              supabase
                .from("contractor_interests")
                .select("id, job_id, contractor_id, status, submitted_at, closed_at, close_reason")
                .order("submitted_at", { ascending: false }),
            ]);
            if (offersRes.error) {
              console.warn("Failed to refresh marketplace offers from realtime signal:", offersRes.error);
              setMarketplaceOffers([]);
            } else {
              const mappedOffers = ((offersRes.data ?? []) as DbMarketplaceOfferRow[])
                .map(mapMarketplaceOfferRow)
                .filter((v): v is MarketplaceOffer => Boolean(v));
              setMarketplaceOffers(mappedOffers);
            }
            if (interestsRes.error) {
              console.warn("Failed to refresh contractor interests from realtime signal:", interestsRes.error);
              setContractorInterests([]);
            } else {
              const mappedInterests = ((interestsRes.data ?? []) as DbContractorInterestRow[])
                .map(mapContractorInterestRow)
                .filter((v): v is ContractorInterest => Boolean(v));
              setContractorInterests(mappedInterests);
            }
            refreshedFromRealtime = true;
          }
          if (tables.has("job_spec_executions") || tables.has("job_spec_execution_photos") || tables.has("execution_proofs") || tables.has("job_checklist_items")) {
            try {
              const [jobsMetaRes, executionRootsRes] = await Promise.all([
                supabase.from("jobs").select("id, metadata"),
                supabase.from("job_spec_executions").select("job_id, spec_ref, spec_version, metadata"),
              ]);
              const rows = jobsMetaRes.error ? [] : ((jobsMetaRes.data ?? []) as DbJobMetadataRow[]);
              const jobIdBatch3 = rows.map((r) => r.id);
              const checklistItemsRes3 = jobIdBatch3.length > 0
                ? await supabase
                    .from("job_checklist_items")
                    .select("job_id, phase_key, item_index, checked")
                    .in("job_id", jobIdBatch3)
                : { data: [] as { job_id: string; phase_key: string; item_index: number; checked: boolean }[], error: null };
              const checklistByJob3 = new Map<string, Record<string, boolean>>();
              if (!checklistItemsRes3.error && checklistItemsRes3.data) {
                for (const r of checklistItemsRes3.data as { job_id: string; phase_key: string; item_index: number; checked: boolean }[]) {
                  if (!checklistByJob3.has(r.job_id)) checklistByJob3.set(r.job_id, {});
                  checklistByJob3.get(r.job_id)![`${r.phase_key}:${r.item_index}`] = r.checked;
                }
              }
              const metadataExecs = rows
                .map(mapSpecExecutionFromJobMetadata)
                .filter((v): v is JobSpecExecution => Boolean(v));
              const tableExecs = executionRootsRes.error
                ? []
                : ((executionRootsRes.data ?? []) as DbJobSpecExecutionRow[])
                    .map(mapSpecExecutionFromExecutionRow)
                    .filter((v): v is JobSpecExecution => Boolean(v));
              const tableJobs = new Set(tableExecs.map((e) => e.jobId));
              const fallbackExecs = metadataExecs.filter((e) => !tableJobs.has(e.jobId));
              const baseExecMap3 = new Map<string, JobSpecExecution>(
                [...tableExecs, ...fallbackExecs].map((e) => [e.jobId, e]),
              );
              const execs: JobSpecExecution[] = [];
              const patchedIds3 = new Set<string>();
              for (const row of rows) {
                const meta = row.metadata ?? {};
                const bound = (meta.boundJobSpec ?? meta.bound_job_spec) as BoundJobSpec | undefined;
                const checklist = checklistByJob3.get(row.id);
                if (!checklist) continue;
                const existing3 = baseExecMap3.get(row.id) ?? null;
                const result = bound
                  ? applyPortalChecklistToExec(row.id, bound, checklist, existing3)
                  : synthesizeExecFromPortalChecklist(row.id, checklist, existing3);
                if (result) { execs.push(result); patchedIds3.add(row.id); }
              }
              for (const e of [...tableExecs, ...fallbackExecs]) {
                if (!patchedIds3.has(e.jobId)) execs.push(e);
              }
              specExecutionSigRef.current = new Map(
                execs.map((e) => [e.jobId, stableSerialize(e)]),
              );
              setSpecExecutions((prev) => mergeSpecExecutionsPreservingLocalEdits(execs, prev));
              refreshedFromRealtime = true;
            } catch (err) {
              console.warn("Failed to refresh spec executions from realtime signal:", err);
            }
          }
          if (refreshedFromRealtime) {
            setLastRefreshedAt(new Date().toISOString());
          }
        })();
      }, 1_000);
    };

    const channel = supabase
      .channel("appdata-shared-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "quotes" }, () => scheduleRefresh("quotes"))
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, () => scheduleRefresh("leads"))
      .on("postgres_changes", { event: "*", schema: "public", table: "appointment_results" }, () => scheduleRefresh("appointment_results"))
      .on("postgres_changes", { event: "*", schema: "public", table: "issues" }, (payload) => scheduleRefresh("issues", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "contractors" }, (payload) => scheduleRefresh("contractors", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "crews" }, (payload) => scheduleRefresh("crews", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "follow_up_tasks" }, () => scheduleRefresh("follow_up_tasks"))
      .on("postgres_changes", { event: "*", schema: "public", table: "service_tickets" }, () => scheduleRefresh("service_tickets"))
      .on("postgres_changes", { event: "*", schema: "public", table: "customer_comms" }, () => scheduleRefresh("customer_comms"))
      .on("postgres_changes", { event: "*", schema: "public", table: "build_review_events" }, () => scheduleRefresh("build_review_events"))
      .on("postgres_changes", { event: "*", schema: "public", table: "marketplace_offers" }, () => scheduleRefresh("marketplace_offers"))
      .on("postgres_changes", { event: "*", schema: "public", table: "contractor_interests" }, () => scheduleRefresh("contractor_interests"))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_spec_executions" }, () => scheduleRefresh("job_spec_executions"))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_spec_execution_photos" }, () => scheduleRefresh("job_spec_execution_photos"))
      .on("postgres_changes", { event: "*", schema: "public", table: "execution_proofs" }, () => scheduleRefresh("execution_proofs"))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_checklist_items" }, () => scheduleRefresh("job_checklist_items"))
      .subscribe();

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      void supabase.removeChannel(channel);
    };
  }, [scheduleLatestDataRefresh, validationMode, fetchWithCache, shouldDeferRouteAppDataHydration]);

  // Realtime can be missed after tab sleep/background throttling or transient reconnects.
  // Force a fresh pull when the user returns so cross-device edits appear without manual reload.
  useEffect(() => {
    if (validationMode || shouldDeferRouteAppDataHydration) return;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const triggerFreshSync = (reason: "visibility" | "focus" | "online" | "poll") => {
      recordRealtimeMetric({
        source: "appdata-visible-fallback",
        event: "full-refetch",
        table: "appdata",
        detail: SUPABASE_SYNC_DEBUG ? { reason, intervalMs: VISIBLE_SYNC_POLL_MS } : undefined,
      });
      scheduleLatestDataRefresh();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") triggerFreshSync("visibility");
    };
    const onFocus = () => triggerFreshSync("focus");
    const onOnline = () => triggerFreshSync("online");

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);

    // Light polling fallback while visible; protects against dropped websocket events
    // and keeps the "Last synced" signal from going stale during quiet periods.
    intervalId = setInterval(() => {
      if (document.visibilityState === "visible") triggerFreshSync("poll");
    }, VISIBLE_SYNC_POLL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      if (intervalId) clearInterval(intervalId);
    };
  }, [scheduleLatestDataRefresh, shouldDeferRouteAppDataHydration, validationMode]);

   // ── Persist contractor pay profiles to DB metadata ──
   useEffect(() => {
     if (validationMode || !payProfilesLoadedRef.current) return;
     const changed = payProfiles.filter((profile) => {
       const nextSig = stableSerialize(profile);
       const prevSig = payProfileSigRef.current.get(profile.contractorId);
       return prevSig !== nextSig;
     });
     if (changed.length === 0) return;

     const timer = setTimeout(() => {
       void (async () => {
         beginContractorSync();
         let savedAny = false;
         try {
         const contractorIds = Array.from(new Set(changed.map((p) => p.contractorId)));
         const { data: rows, error } = await supabase
           .from("contractors")
           .select("id, metadata")
           .in("id", contractorIds);
         if (error) {
           console.warn("Failed to load contractor metadata for pay profile persistence:", error);
           return;
         }
         const currentById = new Map<string, Record<string, unknown>>(
           ((rows ?? []) as DbContractorMetadataRow[]).map((r) => [
             r.id,
             ((r.metadata ?? {}) as Record<string, unknown>),
           ]),
         );
         await Promise.all(
           changed.map(async (profile) => {
             const current = currentById.get(profile.contractorId) ?? {};
             const metadata = { ...current, payProfile: profile };
             const { error: updateError } = await supabase
               .from("contractors")
               .update({ metadata })
               .eq("id", profile.contractorId);
             if (updateError) {
               console.warn(`Failed to persist pay profile for contractor ${profile.contractorId}:`, updateError);
               return;
             }
             payProfileSigRef.current.set(profile.contractorId, stableSerialize(profile));
             savedAny = true;
           }),
         );
         } finally {
           endContractorSync(savedAny);
         }
       })();
     }, 250);

     return () => clearTimeout(timer);
   }, [beginContractorSync, endContractorSync, payProfiles, stableSerialize, validationMode]);

   // ── Persist spec executions to DB (table-first; metadata read-fallback still enabled) ──
   useEffect(() => {
     if (validationMode || !specExecutionsLoadedRef.current) return;
     const changed = specExecutions.filter((execution) => {
       const nextSig = stableSerialize(execution);
       const prevSig = specExecutionSigRef.current.get(execution.jobId);
       return prevSig !== nextSig;
     });
     if (changed.length === 0) return;

     const timer = setTimeout(() => {
       void (async () => {
         beginContractorSync();
         let savedAny = false;
         try {
        await Promise.all(
          changed.map(async (execution) => {
            const rootStatus = execution.allPhasesComplete ? "qa_passed" : "in_progress";
            const rootFull = {
              job_id: execution.jobId,
              spec_ref: execution.specRef.specId,
              spec_version: String(execution.specRef.versionNumber),
              status: rootStatus,
              metadata: execution,
              all_phases_complete: execution.allPhasesComplete,
              updated_at: new Date().toISOString(),
            };
            const rootSlim = {
              job_id: execution.jobId,
              spec_ref: execution.specRef.specId,
              spec_version: String(execution.specRef.versionNumber),
              status: rootStatus,
              metadata: execution,
            };
            const rootBare = {
              job_id: execution.jobId,
              spec_ref: execution.specRef.specId,
              spec_version: String(execution.specRef.versionNumber),
              status: rootStatus,
             };

             let write = await supabase
               .from("job_spec_executions")
               .upsert(rootFull, { onConflict: "job_id,spec_ref,spec_version" });

             if (write.error && /all_phases_complete|updated_at/i.test(write.error.message ?? "")) {
               write = await supabase
                 .from("job_spec_executions")
                 .upsert(rootSlim, { onConflict: "job_id,spec_ref,spec_version" });
             }

             if (write.error && /metadata|status/i.test(write.error.message ?? "")) {
               write = await supabase
                 .from("job_spec_executions")
                 .upsert(rootBare, { onConflict: "job_id,spec_ref,spec_version" });
             }

             if (write.error) {
               console.warn(`Failed to persist spec execution table row for job ${execution.jobId}:`, write.error);
               return;
             }
             specExecutionSigRef.current.set(execution.jobId, stableSerialize(execution));
             savedAny = true;
           }),
         );
         } finally {
           endContractorSync(savedAny);
         }
       })();
     }, 250);

     return () => clearTimeout(timer);
   }, [beginContractorSync, endContractorSync, specExecutions, stableSerialize, validationMode]);

   // ── Persist material kits to DB (table-first; metadata read-fallback still enabled) ──
   useEffect(() => {
     if (validationMode) return;
     const changed = materialKits.filter((kit) => {
       const nextSig = stableSerialize(kit);
       const prevSig = materialKitSigRef.current.get(kit.jobId);
       if (prevSig === nextSig) return false;
       // Before the initial DB load completes, only save kits that have actual
       // user modifications (at least one item confirmed or flagged missing).
       // This prevents a fresh all-pending kit from overwriting real DB data
       // while also ensuring confirmed items are never lost to the load race.
       if (!materialKitsLoadedRef.current) {
         return kit.items.some((i) => i.status !== "pending" || !!i.decidedAt);
       }
       return true;
     });
     if (changed.length === 0) return;

     const timer = setTimeout(() => {
       void (async () => {
         beginContractorSync();
         let savedAny = false;
         try {
        await Promise.all(
          changed.map(async (kit) => {
            const { error: kitWriteError } = await supabase
              .from("job_spec_material_kits")
              .upsert(
                {
                  job_id: kit.jobId,
                  spec_ref: kit.specRef.specId,
                  spec_version: String(kit.specRef.versionNumber),
                  metadata: kit,
                   updated_at: new Date().toISOString(),
                 },
                 { onConflict: "job_id,spec_ref,spec_version" },
               );
             if (kitWriteError) {
               console.warn(`Failed to persist material kit table row for job ${kit.jobId}:`, kitWriteError);
               return;
             }
             materialKitSigRef.current.set(kit.jobId, stableSerialize(kit));
             savedAny = true;
           }),
         );
         } finally {
           endContractorSync(savedAny);
         }
       })();
     }, 250);

     return () => clearTimeout(timer);
   }, [beginContractorSync, endContractorSync, materialKits, stableSerialize, validationMode]);

  // ── Lifecycle backlink runtime backfill ──
  // Adds Job.opportunityId / Job.appointmentResultId only when exactly one
  // unambiguous match exists. Never overrides explicit values. Pure derivation —
  // does not mutate seed data.
  const jobsWithBacklinks = useMemo(() => {
    if (jobs.length === 0) return jobs;
    // Build job→opp index from existing forward links.
    const oppByJobId = new Map<string, string>();
    const oppCountByJobId = new Map<string, number>();
    for (const o of normalizedOpps) {
      if (!o.linkedJobId) continue;
      oppCountByJobId.set(o.linkedJobId, (oppCountByJobId.get(o.linkedJobId) || 0) + 1);
      oppByJobId.set(o.linkedJobId, o.id);
    }
    // Build opp→sold-result index (only when exactly one sold result per opp).
    const soldByOpp = new Map<string, { id: string; count: number }>();
    for (const r of appointmentResults) {
      if (r.resultType !== "sold" || !r.opportunityId) continue;
      const cur = soldByOpp.get(r.opportunityId);
      if (!cur) soldByOpp.set(r.opportunityId, { id: r.id, count: 1 });
      else cur.count += 1;
    }
    let dirty = false;
    const out = jobs.map(j => {
      const next: typeof j = j;
      let oppId = j.opportunityId;
      let arId = j.appointmentResultId;
      if (!oppId && oppCountByJobId.get(j.id) === 1) {
        oppId = oppByJobId.get(j.id);
      }
      if (!arId && oppId) {
        const s = soldByOpp.get(oppId);
        if (s && s.count === 1) arId = s.id;
      }
      if (oppId !== j.opportunityId || arId !== j.appointmentResultId) {
        dirty = true;
        return { ...next, opportunityId: oppId, appointmentResultId: arId };
      }
      return next;
    });
    return dirty ? out : jobs;
  }, [jobs, normalizedOpps, appointmentResults]);

   const contextValue = useMemo(() => ({
       jobs: jobsWithBacklinks, setJobs, jobsLoading,
       issues, setIssues, issuesLoading,
       contractors, setContractors, contractorsLoading,
       crews, setCrews,
       // Contractor opportunities (legacy name)
       opportunities: contractorOpps, setOpportunities: setContractorOpps,
       customerComms, setCustomerComms, customerCommsLoading,
       credits, setCredits, creditsLoading,
       callbacks, holdbackLedger, disputeEvents, softSuggestions, overrideOutcomes,
       // Unified sales opportunities (read-time normalized for legacy compat fields)
       salesOpportunities: normalizedOpps, salesOpportunitiesLoading, setSalesOpportunities: setOpps,
       quotes: normalizedOpps, setQuotes: setOpps,
       leads: normalizedOpps, setLeads: setOpps,
       purchaseOrders, setPurchaseOrders, stagingRecords, setStagingRecords,
       loadOutChecklists, setLoadOutChecklists,
       conversations, setConversations, conversationsLoading,
       messages: commsMessages, setMessages: setCommsMessages,
       serviceTickets, setServiceTickets, serviceTicketsLoading,
       materialEvents, setMaterialEvents,
       buildReviewEvents, setBuildReviewEvents, buildReviewEventsLoading, setBuildReviewEventsLoading,
       bathReviewEvents: buildReviewEvents, setBathReviewEvents: setBuildReviewEvents,
       jobMessages, setJobMessages, jobMessagesLoading,
       leadSources, setLeadSources, leadSourcesLoading,
       followUpTasks, setFollowUpTasks,
       installerWrapRecords, setInstallerWrapRecords,
       installFollowUpTasks, setInstallFollowUpTasks,
       appointmentResults, setAppointmentResults,
       appointmentOutcomeExtensions, addAppointmentOutcomeExtension,
       consultationSessions, setConsultationSessions, consultationSessionsLoading,
       payProfiles, setPayProfiles,
       specExecutions, setSpecExecutions,
       materialKits, setMaterialKits,
       specOutcomes,
       marketplaceOffers, setMarketplaceOffers,
       contractorInterests, setContractorInterests,
       markContractorOnboardingComplete,
       systemEvents, setSystemEvents, appendSystemEvents,
       refreshData: refreshLatestData,
       isRefreshing,
       lastRefreshedAt,
       contractorPortalSyncing,
       contractorPortalLastSavedAt,
     }), [
       jobsWithBacklinks, jobsLoading,
       issues, issuesLoading,
       contractors, contractorsLoading,
       crews,
       contractorOpps,
       customerComms, customerCommsLoading,
       credits, creditsLoading,
       normalizedOpps, salesOpportunitiesLoading,
       purchaseOrders, stagingRecords, loadOutChecklists,
       conversations, conversationsLoading,
       commsMessages,
       serviceTickets, serviceTicketsLoading,
       materialEvents,
       buildReviewEvents, buildReviewEventsLoading,
       jobMessages, jobMessagesLoading,
       leadSources, leadSourcesLoading,
       followUpTasks,
       installerWrapRecords,
       installFollowUpTasks,
       appointmentResults,
       appointmentOutcomeExtensions, addAppointmentOutcomeExtension,
       consultationSessions, consultationSessionsLoading,
       payProfiles,
       specExecutions,
       materialKits,
       specOutcomes,
       marketplaceOffers,
       contractorInterests,
       markContractorOnboardingComplete,
       systemEvents, appendSystemEvents,
       refreshLatestData,
       isRefreshing,
       lastRefreshedAt,
       contractorPortalSyncing,
       contractorPortalLastSavedAt,
     ]);

  const jobsDataValue: JobsData = useMemo(
    () => ({
      jobs: contextValue.jobs,
      setJobs: contextValue.setJobs,
      jobsLoading: contextValue.jobsLoading,
      issues: contextValue.issues,
      setIssues: contextValue.setIssues,
      issuesLoading: contextValue.issuesLoading,
      buildReviewEvents: contextValue.buildReviewEvents,
      setBuildReviewEvents: contextValue.setBuildReviewEvents,
      buildReviewEventsLoading: contextValue.buildReviewEventsLoading,
      setBuildReviewEventsLoading: contextValue.setBuildReviewEventsLoading,
      installerWrapRecords: contextValue.installerWrapRecords,
      setInstallerWrapRecords: contextValue.setInstallerWrapRecords,
      installFollowUpTasks: contextValue.installFollowUpTasks,
      setInstallFollowUpTasks: contextValue.setInstallFollowUpTasks,
      payProfiles: contextValue.payProfiles,
      setPayProfiles: contextValue.setPayProfiles,
      specExecutions: contextValue.specExecutions,
      setSpecExecutions: contextValue.setSpecExecutions,
      materialKits: contextValue.materialKits,
      setMaterialKits: contextValue.setMaterialKits,
      specOutcomes: contextValue.specOutcomes,
    }),
    [contextValue],
  );

  const salesDataValue: SalesData = useMemo(
    () => ({
      salesOpportunities: contextValue.salesOpportunities,
      setSalesOpportunities: contextValue.setSalesOpportunities,
      quotes: contextValue.quotes,
      setQuotes: contextValue.setQuotes,
      leads: contextValue.leads,
      setLeads: contextValue.setLeads,
      appointmentResults: contextValue.appointmentResults,
      setAppointmentResults: contextValue.setAppointmentResults,
      appointmentOutcomeExtensions: contextValue.appointmentOutcomeExtensions,
      addAppointmentOutcomeExtension: contextValue.addAppointmentOutcomeExtension,
      followUpTasks: contextValue.followUpTasks,
      setFollowUpTasks: contextValue.setFollowUpTasks,
      leadSources: contextValue.leadSources,
      setLeadSources: contextValue.setLeadSources,
      leadSourcesLoading: contextValue.leadSourcesLoading,
      consultationSessions: contextValue.consultationSessions,
      setConsultationSessions: contextValue.setConsultationSessions,
      consultationSessionsLoading: contextValue.consultationSessionsLoading,
    }),
    [contextValue],
  );

  const opsDataValue: OpsData = useMemo(
    () => ({
      contractors: contextValue.contractors,
      setContractors: contextValue.setContractors,
      contractorsLoading: contextValue.contractorsLoading,
      crews: contextValue.crews,
      setCrews: contextValue.setCrews,
      customerComms: contextValue.customerComms,
      setCustomerComms: contextValue.setCustomerComms,
      customerCommsLoading: contextValue.customerCommsLoading,
      serviceTickets: contextValue.serviceTickets,
      setServiceTickets: contextValue.setServiceTickets,
      serviceTicketsLoading: contextValue.serviceTicketsLoading,
      conversations: contextValue.conversations,
      setConversations: contextValue.setConversations,
      messages: contextValue.messages,
      setMessages: contextValue.setMessages,
      marketplaceOffers: contextValue.marketplaceOffers,
      setMarketplaceOffers: contextValue.setMarketplaceOffers,
      contractorInterests: contextValue.contractorInterests,
      setContractorInterests: contextValue.setContractorInterests,
      systemEvents: contextValue.systemEvents,
      setSystemEvents: contextValue.setSystemEvents,
      appendSystemEvents: contextValue.appendSystemEvents,
      markContractorOnboardingComplete: contextValue.markContractorOnboardingComplete,
    }),
    [contextValue],
  );

  const inventoryDataValue: InventoryData = useMemo(
    () => ({
      purchaseOrders: contextValue.purchaseOrders,
      setPurchaseOrders: contextValue.setPurchaseOrders,
      stagingRecords: contextValue.stagingRecords,
      setStagingRecords: contextValue.setStagingRecords,
      loadOutChecklists: contextValue.loadOutChecklists,
      setLoadOutChecklists: contextValue.setLoadOutChecklists,
      materialEvents: contextValue.materialEvents,
      setMaterialEvents: contextValue.setMaterialEvents,
    }),
    [contextValue],
  );

  return (
    <AppDataContext.Provider value={contextValue}>
      <JobsDataContext.Provider value={jobsDataValue}>
        <SalesDataContext.Provider value={salesDataValue}>
          <OpsDataContext.Provider value={opsDataValue}>
            <InventoryDataContext.Provider value={inventoryDataValue}>
              {children}
            </InventoryDataContext.Provider>
          </OpsDataContext.Provider>
        </SalesDataContext.Provider>
      </JobsDataContext.Provider>
    </AppDataContext.Provider>
  );
}

export function useAppData() {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be inside AppDataProvider");
  return ctx;
}

export function useOptionalAppData() {
  return useContext(AppDataContext);
}

export function useJobsData() {
  const ctx = useContext(JobsDataContext);
  if (!ctx) throw new Error("useJobsData must be inside AppDataProvider");
  return ctx;
}

export function useSalesData() {
  const ctx = useContext(SalesDataContext);
  if (!ctx) throw new Error("useSalesData must be inside AppDataProvider");
  return ctx;
}

export function useOpsData() {
  const ctx = useContext(OpsDataContext);
  if (!ctx) throw new Error("useOpsData must be inside AppDataProvider");
  return ctx;
}

export function useInventoryData() {
  const ctx = useContext(InventoryDataContext);
  if (!ctx) throw new Error("useInventoryData must be inside AppDataProvider");
  return ctx;
}
